/**
 * @data-dashboard/shared — CARD CATALOG (founder 2026-07-12; merged 2026-07-13)
 *
 * ONE versioned, in-code entry per UNIQUE client-facing dashboard card. This is
 * the "approve every card" reference the founder asked for: the plain-language
 * definition, the arithmetic in words, and a worked example with reconciling
 * numbers — so a card can be read, understood, and approved ONE at a time.
 *
 * MERGED (founder 2026-07-13: "why double up on cards? ... I shouldn't have to
 * approve the same card twice. Please merge all."). A card that shows in more
 * than one place (MER on Exec + Marketing; CRV/PIP on the cockpit + Financials;
 * the Customer-LTV breakdown's four tabs; the subscription-money summary's three
 * figures) is ONE entry with a `placements: [{ pageKey, renderId }]` array — one
 * approval covers every placement. Merging only touched title/placements/note —
 * never the definition/arithmetic/example — so no already-approved card's hash
 * moved and nothing re-opened for review. The nine Operations REFUND cards are
 * intentionally left as separate entries (a parallel consolidation owns them).
 *
 * WHY IT LIVES IN /shared. Both the operator console (apps/api → /admin/cards)
 * and the client app (card render gate) need the same list + approval math, so
 * there is exactly one source of truth and the API test-suite can cover it.
 *
 * HOW APPROVAL WORKS (the grandfather rule).
 *  - Every card in SHIP_SET_CARD_IDS stays VISIBLE while it's still "pending" —
 *    approve at your own pace, nothing disappears for a live client. Only an
 *    explicit REJECT hides a grandfathered card.
 *  - A card added LATER (in the catalog, NOT in the ship set) defaults to
 *    hidden-until-approved on real client accounts.
 *  - Demo / test / resettable accounts always see everything (empty hidden set).
 *  - "Changed since approval" (the stored hash no longer matches) re-opens a card.
 *
 * The title is DISPLAY-only and is NOT part of the hash — the operator can rename
 * a card (card_approvals.display_name) without ever re-opening approval.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A dashboard page, keyed by its URL segment (the HideProvider `pageKey`). */
export type CardPageKey =
  | "dashboard"
  | "financials"
  | "marketing"
  | "products"
  | "cohorts"
  | "retention"
  | "subscriptions"
  | "operations";

/** One place a card renders. A card can have several (it shows on >1 page, or a
 *  page lists it under several tabs). */
export interface CardPlacement {
  /** The HideProvider pageKey (URL segment). */
  pageKey: CardPageKey;
  /** Human page name (group header / "also on" label). */
  page: string;
  /** The per-page id the card emits as `data-card-anchor` — the client render
   *  gate hides by it, "See it live" scrolls/flashes it, and (when it's a single
   *  titled card, not a section wrapper) a rename override replaces its title. */
  renderId: string;
}

export interface CardCatalogEntry {
  /** Stable, globally-unique slug — the approval primary key. */
  id: string;
  /** Founder-facing card BASE title (DISPLAY only — never part of the hash). The
   *  live card may append a period/window suffix — see `titleSuffix`. */
  title: string;
  /** How the live card renders its title suffix, so the review row can show the
   *  title the way the founder sees it on the page (DISPLAY only, never hashed):
   *    - "{timeline}" → the card appends "· <window picker label>" (most KPI/Metric
   *      titles). The review shows the literal "{timeline}" placeholder token.
   *    - any other string → a FIXED suffix the card always shows (e.g. "right now",
   *      "last 30 days", "all-time", "current").
   *    - undefined → the title renders plain, no suffix.
   *  The editable title override still edits ONLY the base title. */
  titleSuffix?: string;
  /** Everywhere this one card renders; placements[0] is the primary placement. */
  placements: CardPlacement[];
  /** VERY SIMPLE, plain-language "what this number means". CLIENT-VISIBLE: this is
   *  the "What it is" line the live ⓘ resolves to (operator override → this →
   *  component fallback). Keep it client voice — no engineer-speak. */
  definition: string;
  /** The formula in words. */
  arithmetic: string;
  /** A concrete worked example whose numbers reconcile. */
  example: string;
  /** Whether the card follows the date picker, and if not, why. */
  windowBehavior: string;
  /** Registry metric ids this card is built from, where one applies. */
  metricIds: string[];
  /** The CLIENT-VISIBLE "Note" line — the SAME text the live card's ⓘ shows (the
   *  live ⓘ resolves it: operator override → this → component fallback). Client
   *  voice ONLY; empty when the live card has no Note. Operator/engineering
   *  commentary goes in `opsNote`, NEVER here. */
  note?: string;
  /** OPERATOR-ONLY meta (a consolidation, a build caveat, "library-only", an
   *  exemption, plumbing). Shown on the review row as small meta text near the
   *  status chip — NEVER in the client ⓘ or the client-copy boxes. */
  opsNote?: string;
  /** Original card ids merged INTO this canonical entry (approval remap source). */
  mergedFrom?: string[];
}

/** Approval row shape (mirrors the card_approvals table, provider-agnostic). */
export type CardApprovalStatus = "approved" | "rejected";
export interface CardApprovalRow {
  cardId: string;
  status: CardApprovalStatus;
  definitionHash: string;
}

/** The status the operator console shows per card. 'question' = the founder
 *  asked a question (the TBD tab). */
export type CardDerivedStatus = "approved" | "pending" | "rejected" | "changed" | "question";

// ---------------------------------------------------------------------------
// Stable definition hash (pure, browser-safe — no node:crypto)
// ---------------------------------------------------------------------------

/**
 * Deterministic FNV-1a 32-bit hash → 8-hex-char string. A change-detector: if
 * definition/arithmetic/example change, the hash changes, so an already-approved
 * card flips to "changed since approval". The TITLE is deliberately excluded, so
 * a rename never re-opens approval.
 */
export function stableHash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/** The hash of a card's approvable content (definition + arithmetic + example).
 *  Title/placements/note are NOT included — display changes never re-open a card. */
export function cardDefinitionHash(entry: Pick<CardCatalogEntry, "definition" | "arithmetic" | "example">): string {
  return stableHash(`${entry.definition}␟${entry.arithmetic}␟${entry.example}`);
}

// ---------------------------------------------------------------------------
// The catalog
// ---------------------------------------------------------------------------

export const CARD_CATALOG: CardCatalogEntry[] = [
  {
    id: "exec.revenue",
    title: "Revenue",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "revenue" },
    ],
    definition: "The sales money you brought in, or the contribution margin (CM) those exact sales kept after variable costs. Revenue is shown net by default: refunds and sales tax come out while shipping stays in. The arrow compares your chosen view with the period just before.",
    arithmetic: "Revenue = the selected orders' totals minus sales tax and refunds by default. CM = those same orders' per-SKU sales minus their per-SKU product, shipping, payment, fulfillment, and other variable costs. When a store supplies one refund total for the whole order, each view subtracts only the part that belongs to the sales dollars it counts; cash-out cards keep the full refund. The Revenue / CM choice changes only the dollar basis, not the orders or dates.",
    example: "Vital Roots, year-to-date: Revenue = $1,117,474 and CM = $611,997 after the variable costs tied to those same sales.",
    windowBehavior: "Follows the timeline you pick.",
    metricIds: ["revenue", "cm_amount"],
    note: "Pick a product to narrow both views. CM turns on after every selling product has costs. Revenue is net by default. MER uses gross revenue on purpose, so it will not tie to the penny.",
    mergedFrom: ["exec.daypart-strip"],
  },
  {
    id: "exec.orders",
    title: "Orders",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "orders" },
      { pageKey: "marketing", page: "Marketing", renderId: "orders" },
    ],
    definition: "How many orders came in over the dates you picked.",
    arithmetic: "Count every order dated inside the window (refunded orders still count — a refund removes the money, not the order).",
    example: "Vital Roots, year-to-date: 15,018 orders. The down-arrow 1.7% compares to the previous same-length period (15,276).",
    windowBehavior: "Follows the timeline you pick.",
    metricIds: ["orders"],
    note: "Narrows with the product filter — pick a product and this counts only orders that included it. Refunded orders still count — a refund takes back the money, not the order. The bars below split orders by time of day, in your store's timezone.",
    mergedFrom: ["marketing.orders"],
  },
  {
    id: "exec.customers",
    title: "Customers",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "customers" },
    ],
    definition: "How many different people bought in the range, split into brand-new and returning. A customer with several emails/IDs counts once.",
    arithmetic: "Count distinct customers (merged identities) with an order in the window; 'new' = their first-ever order landed in the window; returning = the rest. With a product selected, a customer is new for that product only when their first-ever order included it.",
    example: "Vital Roots, year-to-date: 7,342 customers — 5,065 new + 2,277 returning (7,342 − 5,065 = 2,277).",
    windowBehavior: "Follows the timeline you pick.",
    metricIds: ["customers", "new_customers"],
    note: "Pick a product and this counts customers who bought it in the window. 'New' means their first-ever order included that product; someone who first bought something else and added this product later counts as returning. A customer with several IDs counts once. Refunds don't remove a customer.",
  },
  {
    id: "exec.repeat-rate",
    title: "Repeat rate",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "repeat" },
      { pageKey: "retention", page: "Retention", renderId: "ret-repeat" },
    ],
    definition: "Of one fully matured customer group, the share who placed a second order within the measurement window. It is the true cohort-based 'do they stick?' number.",
    arithmetic: "Standard date choices use the newest customer group that has fully completed the selected horizon. A 1-year view uses customers acquired 12–24 months ago and measures their first year. A custom range keeps the exact acquisition dates you chose, then includes only customers who have completed that full custom-length runway.",
    example: "Vital Roots, 1-year pick: of customers acquired 12–24 months ago, the share who bought again within their first year.",
    windowBehavior: "The picker chooses the acquisition group and the measurement horizon. Standard choices use the newest fully completed group. Custom dates keep the exact acquisition dates. The card states the exact group in plain words. 'All time' = 2+ orders ever among customers at least 30 days old.",
    metricIds: [],
    note: "Narrows with the product filter — it keeps only customers whose FIRST order included the product. Based on order counts, so costs don't change it. Same number as the Retention page's Repeat rate.",
    opsNote: "One card, two placements (Exec 'repeat' Kpi + Retention 'ret-repeat' Tile). Its live ⓘ is component-sourced (the What/Note interpolate the exact cohort shown + the older any-era lens %), so it stays component-owned — a documented single-source exception.",
    mergedFrom: ["retention.repeat-rate"],
  },
  {
    id: "exec.refund-rate",
    title: "Refund rate",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "refund" },
    ],
    definition: "The share of the window's orders that came back as refunds — a quick read on product or fulfillment problems.",
    arithmetic: "Orders placed in the window that have a completed refund (whenever the refund landed) ÷ all orders placed in the window. Counts whole orders, not dollars.",
    example: "Vital Roots, year-to-date: 681 refunded ÷ 15,018 orders = 4.5%.",
    windowBehavior: "Follows the timeline you pick. Recent windows can keep rising as late refunds land against their orders.",
    metricIds: ["refund_rate"],
    note: "Narrows with the product filter — it counts only orders containing that product. Moves with the date picker now (it used to be pinned to all-time). This counts ORDERS, not refunded dollars. For the full refund-timing breakdown, see the Operations page.",
  },
  {
    id: "exec.mer",
    title: "MER (blended)",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "mer" },
      { pageKey: "marketing", page: "Marketing", renderId: "mer-blended" },
    ],
    definition: "For every dollar spent on marketing, how much Revenue or contribution margin (CM) comes back. Revenue is the standard blended marketing read. CM shows whether those sales still pay after their variable costs.",
    arithmetic: "Revenue view = gross revenue with refunds left in ÷ all marketing spend. CM view = contribution margin from the same dates ÷ the same marketing spend. Only the top of the ratio changes.",
    example: "Vital Roots, year-to-date: Revenue MER is about 4.2× and CM-MER is about 2.2× after variable costs.",
    windowBehavior: "Follows the timeline you pick. Blended — a product filter won't move it (spend isn't tracked per product).",
    metricIds: ["mer", "revenue_gross", "cm_amount"],
    note: "Blended, so a product filter will not move it because spend is not tracked by product. Revenue leaves refunds in to match the standard MER benchmark. CM needs complete product costs. Both views need marketing spend.",
    mergedFrom: ["marketing.mer", "marketing.cm-mer"],
  },
  {
    id: "exec.acquisition-cac",
    title: "Acquisition (CAC)",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "cac" },
    ],
    definition: "The cost to get one brand-new customer, shown as a blended average across all traffic channels. Drill in to view by channel.",
    arithmetic: "Acquisition spend (ad spend + affiliate commissions on customers' FIRST orders) ÷ brand-new customers in the period. Commissions on repeat orders are a retention cost and are left out of acquisition.",
    example: "Vital Roots, year-to-date: $273,304 acquisition spend ÷ 5,065 new customers = $53.96.",
    windowBehavior: "Follows the timeline you pick. Blended — acquisition spend isn't tracked per product. Very short recent windows read high (spend counted, newest customers still arriving).",
    metricIds: ["cac"],
    note: "This measures new-acquisition cost only. Affiliate commissions on repeat orders are left out, because they're a cost of KEEPING customers, so they land in your whole-business totals like Marketing Efficiency Ratio (MER) and Your Profit Model instead.",
  },
  {
    id: "exec.operational-drag",
    title: "Operational drag",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "op-drag" },
      { pageKey: "operations", page: "Operations", renderId: "operational-drag" },
    ],
    definition: "How much of your contribution margin gets eaten by fixed overhead. Lower is better, and as you grow more of that margin should survive to the bottom line.",
    arithmetic: "Effective-dated overhead across the window ÷ contribution margin across the same dates. Mid-month overhead changes are prorated by days. All time stops at the real data span instead of stretching across an empty calendar.",
    example: "Vital Roots, year-to-date (6 months): $22,000/mo × 6 = $132,000 overhead ÷ $611,997 margin = 0.22×.",
    windowBehavior: "Follows the timeline you pick. Both halves grow with the window, so the ratio stays comparable across window sizes (it still moves with each period's real margin); on 'All time' overhead is capped to your real data span so it can't balloon. Blended — overhead and costs aren't tracked per product.",
    metricIds: ["cm_amount"],
    note: "This is blended, so a product filter won't move it. It needs overhead and product costs. If only one or two overhead months are recorded, the card warns that earlier dates use the earliest known amount and are still estimates.",
    mergedFrom: ["operations.operational-drag"],
  },
  {
    id: "exec.top-products",
    title: "Top products by revenue",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "top-products" },
    ],
    definition: "Your biggest product families by Revenue or contribution margin (CM). Click any slice to dig in.",
    arithmetic: "Roll each SKU's item revenue or per-SKU CM up to its product family, rank biggest-first, and show the top 6. Revenue / CM changes only the dollar basis and ranking.",
    example: "Vital Roots, year-to-date: Daily Greens $436,318, Deep Sleep $254,781, Radiant Collagen $133,025, Omega Gold $77,735, Gut Guardian $76,104, Wellness Reset $59,220.",
    windowBehavior: "Follows the timeline you pick. Item-level revenue before refunds net out, so family totals won't sum exactly to the headline Revenue card.",
    metricIds: ["revenue", "cm_amount"],
    note: "Shows your top 6 only. Revenue will not add up to the headline because refunded sales still count here and shipping does not. CM appears once your costs are built.",
    opsNote: "Live title flips to \"Top products by CM\" when Revenue / CM is set to CM.",
  },
  {
    id: "exec.revenue-by-month",
    title: "Revenue by month",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "revenue-trend" },
    ],
    definition: "Your Revenue or contribution margin (CM) drawn month by month, so you can spot the direction and busy seasons at a glance.",
    arithmetic: "Group the same selected orders by month. Revenue uses the dashboard's selected revenue basis. CM uses those same orders and subtracts each SKU's variable costs. When a store supplies one whole-order refund total, each view subtracts only the part that belongs to the sales dollars it counts. Revenue / CM changes only the dollar basis.",
    example: "Vital Roots, year-to-date: Revenue averages about $186,000 a month. CM shows the smaller amount left after variable costs.",
    windowBehavior: "Follows the timeline you pick and shows the months inside it. The current month is incomplete and usually reads low.",
    metricIds: ["revenue", "cm_amount"],
    note: "Pick a product to narrow both views. CM turns on after every selling product has costs. The current month is still filling in.",
    opsNote: "In the card library, not the default Exec layout. Uses the shared Revenue / CM breakdown so the monthly rows reconcile with the selected headline basis.",
  },
  {
    id: "exec.time-between-purchases",
    title: "Time between purchases",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "time-between" },
      { pageKey: "retention", page: "Retention", renderId: "ret-time-between" },
    ],
    definition: "The average wait between each order for customers who reached that next order. Smaller gaps at later steps mean the buying habit is getting stronger. Its first bar is the old Days to 2nd order number.",
    arithmetic: "Use the same fully completed customer group and runway as Repeat rate. Standard choices use the newest completed group; custom dates keep the acquisition dates you chose. Within that runway, line up each customer's orders and average the wait at each step among customers who reached it.",
    example: "If 400 customers reached order two after an average of 52 days, the first bar shows 52 days and 400 customers. Later bars usually have fewer customers because fewer people reach those orders.",
    windowBehavior: "Cohort-based. A standard 30-day pick uses the newest fully completed 30-day group. A custom range keeps the customers acquired during those exact dates and measures them over a runway the same length. All time uses full known history among customers at least 30 days old.",
    metricIds: [],
    note: "Pick a product and this keeps only customers whose FIRST order included it. It no longer uses a recent group's few fastest returners, and it does not make up a recommended reminder day. Absorbs the old Days to 2nd order tile as its first bar.",
    mergedFrom: ["retention.time-between-orders", "retention.days-to-2nd-order"],
  },
  {
    id: "exec.profit-equation",
    title: "Your profit model",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Exec dashboard", renderId: "equation-strip" },
      { pageKey: "financials", page: "Financials", renderId: "fin-waterfall" },
    ],
    definition: "A left-to-right bar that walks from your revenue down through each cost to what you actually keep, so you see exactly where the money goes. Any cost you haven't set up yet shows as a labeled empty slot, never a guess.",
    arithmetic: "Whole-company net revenue − variable costs (the gap between revenue and contribution margin) − marketing spend − overhead (totaled across the window; on 'All time', totaled across your real data span so it fills in instead of going blank) = operating profit. When a store supplies one whole-order refund total, Revenue and contribution margin subtract only the part that belongs to the sales dollars this card counts. Every stage is whole-company, so a product filter never moves it.",
    example: "Vital Roots, year-to-date: $1,117,474 revenue − $505,477 variable → $611,997 margin − $278,380 marketing − $132,000 overhead ≈ $201,617 operating profit.",
    windowBehavior: "Follows the timeline you pick, including 'All time' — there it totals overhead across your real data span. Revenue is always your real number. Any missing cost stage shows as an empty, labeled slot, never a guess. When overhead history covers only one or two recorded months, a note says earlier operating profit is still an estimate. The note disappears at three months.",
    metricIds: ["revenue", "cm_amount", "cac"],
    note: "This is a blended average, so a product filter won't move it. Profit only fills in once product costs, marketing, and overhead are all present. A thin-overhead-history note appears only for one or two recorded months, then gets out of the way.",
    opsNote: "Redesigned 2026-07-13: the old 'sample' fill-ins are gone — an input you haven't entered shows as an empty slot that names the gap and links the fix, never padded numbers. One card, two placements (Exec equation-strip + Financials fin-waterfall).",
    mergedFrom: ["financials.profit-waterfall"],
  },
  {
    id: "profit-model.contribution-margin",
    title: "Contribution margin",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Profit model (Exec)", renderId: "pm-contribution-margin" },
    ],
    definition: "The profit left from each sale after the costs that move with it (product cost, shipping, fees) — before fixed overhead. Computed per SKU from your actual cost inputs, never a blended guess.",
    arithmetic: "Revenue − all variable costs, summed over the window's orders. When a store supplies one whole-order refund total, contribution margin subtracts only the part that belongs to the sales dollars it counts; cash-out cards keep the full refund. CM% divides by the revenue of COSTED products only — revenue with no cost data is excluded from the % and disclosed, never counted as $0 margin.",
    example: "Vital Roots, year-to-date: $611,997 of contribution margin; CM% = $611,997 ÷ $1,117,474 costed revenue ≈ 54.8%.",
    windowBehavior: "Follows the timeline you pick. Blended — the product filter does NOT change it (the profit model is a company-level view). Products still missing costs show as a warning that opens the costs chat — unknown is never shown as zero.",
    metricIds: ["cm_amount", "cm_percent"],
    note: "Blended — a company-wide average across every order, the same as the rest of the profit model, so the product filter doesn't move it. It always uses each item's real entered costs, never revenue × an average margin %. To see one product's own margin, use the Products page. The single most powerful lever — a 1-point gain in CM is worth roughly 4–6% more net profit, and it lifts every other lever. Click for CM% by month.",
  },
  {
    id: "profit-model.acquisition-economics",
    title: "Acquisition economics",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Profit model (Exec)", renderId: "pm-acquisition-economics" },
    ],
    definition: "For every dollar you spend to acquire, how much FIRST-ORDER contribution margin your new customers bring back. Above 1× means acquisition pays for itself in margin on day one. The payoff from reorders shows up in Lifetime Value to Customer Acquisition Cost (LTV:CAC), not here.",
    arithmetic: "New customers' true first-order contribution margin, summed from each customer's actual first order, ÷ acquisition spend from those same dates. Acquisition spend is ad spend plus affiliate commissions on first orders. Repeat-order commissions stay out because they are a retention cost. A whole-order refund subtracts only the part that belongs to the sales dollars contribution margin counts. This is not first-order revenue multiplied by a blended margin percentage.",
    example: "Vital Roots, year-to-date: about $250,900 of real first-order margin ÷ $273,304 acquisition spend = 0.92×.",
    windowBehavior: "Follows the literal dates you pick. This is a flow metric, so a 90-day pick uses new customers and acquisition spend from those 90 calendar days. LTV:CAC and Capital Return Velocity use a separate fully completed customer group because those cards need time to mature. If the dates reach before reliable acquisition-cost history, the card starts at the trusted month and shows a warning. Missing spend never counts as $0.",
    metricIds: ["cac", "cm_amount"],
    note: "Above 1× means the first order alone already covers what you paid to acquire. The reorder payoff isn't in here, it shows up in the profit-based LTV:CAC card, which is why a number near 1× can sit happily next to a 2:1 there. Click for the month-by-month trend.",
    opsNote: "First-order margin is the true per-order figure, not a blended estimate. The denominator is acquisition spend from the literal picker dates and excludes repeat-order affiliate commissions. LTV:CAC and payback intentionally use the completed-cohort basis instead.",
  },
  {
    id: "profit-model.operational-efficiency",
    title: "Operational efficiency",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Profit model (Exec)", renderId: "pm-operational-efficiency" },
    ],
    definition: "How well your business turns margin into profit after fixed costs, the flip side of operational drag.",
    arithmetic: "1 − (overhead ÷ contribution margin), over the window.",
    example: "Vital Roots, year-to-date: with drag at 0.22×, efficiency reads about 78%.",
    windowBehavior: "Follows the timeline you pick. Blended — overhead isn't tracked per product.",
    metricIds: ["cm_amount"],
    note: "This is blended, so a product filter won't move it. At 0% overhead eats all your margin. Higher is better. If overhead history is thin, the card warns that earlier dates are still estimates.",
  },
  {
    id: "profit-model.payback-curve",
    title: "Capital return velocity",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Profit model (Exec)", renderId: "pm-payback-curve" },
      { pageKey: "financials", page: "Financials", renderId: "fin-payback-layers" },
    ],
    definition: "Our signature cash card. It shows how long a dollar stays out of your bank account as it moves through inventory, acquisition, overhead, and payout.",
    arithmetic: "Capital Return Velocity = your own cash days in inventory + the days for Contribution Margin to cover acquisition and then overhead + payout lag. Products that tie up more product-cost dollars carry more weight in inventory time. Acquisition is what the completed customer group cost to acquire. Overhead is the effective-dated overhead from those same acquisition dates divided among active buyers in those months. Product cost is already removed inside Contribution Margin, so inventory is counted as time and never charged twice. A whole-order refund subtracts only the part that belongs to the sales dollars Contribution Margin counts.",
    example: "26 days in inventory + 1 day to recover acquisition + 36 more days to recover overhead + 6 days for payout = 69 days, or 2.3 months.",
    windowBehavior: "The picker chooses the measurement horizon. The card uses the most recent customer group that has fully completed that horizon, and it uses the acquisition costs from those customers' acquisition dates. If those dates reach before cost tracking began, the card shows a prominent warning and the safe fix options. Missing spend never counts as $0.",
    metricIds: ["payback_cost_floor", "payback_acquisition_per_customer", "payback_overhead_per_customer"],
    note: "This is blended, so a product filter will not move it. Acquisition and overhead stay separate because they are different levers. Each active buyer gets one share of that month's overhead; time alone does not add another charge. Thin overhead history triggers a warning. If customers have not paid back, the card shows progress instead of making up a date.",
    opsNote: "THE flagship Capital-Return-Velocity card, rendered identically on Exec and Financials. Cash-only by founder ruling 2026-07-16. Inventory is a time stage before the sale, never a dollar threshold on top of CM. Acquisition and overhead remain separate thresholds. Payout lag is added after customer payback.",
    mergedFrom: ["financials.cash-cycle", "financials.payback-layers"],
  },
  {
    id: "profit-model.capital-return-velocity",
    title: "Capital return velocity",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Profit model (Exec)", renderId: "pm-capital-return-velocity" },
      { pageKey: "financials", page: "Financials", renderId: "fin-crv" },
    ],
    definition: "How long a dollar you put out (inventory, acquisition, overhead) stays gone before it comes back as cash — our signature speed-of-money metric, in months.",
    arithmetic: "Days your own cash sits in stock, after supplier terms and financing, weighted by the product-cost dollars tied up in each SKU + days for Contribution Margin to recover that completed customer group's acquisition cost and its share of overhead from the same acquisition dates + payout lag. Inventory is time, not a second product-cost charge. A whole-order refund subtracts only the part that belongs to the sales dollars Contribution Margin counts.",
    example: "If your own cash sits in stock for 26 days, customers recover acquisition and overhead in 37 days, and payouts take 6 days, the full cash round trip is 69 days, or about 2.3 months.",
    windowBehavior: "The picker chooses the measurement horizon. The tile uses the most recent customer group that has fully completed that horizon, with acquisition and overhead from those same dates. If they have not reached full payback, the tile shows no figure and the full card shows their progress instead of guessing.",
    metricIds: ["payback_cost_floor"],
    note: "This is a blended average, so a product filter won't move it. The lower this is, the faster you get your cash back and can put it to work again. Click for the full breakdown.",
    opsNote: "The COMPACT CRV number tile (LeverTile on Exec pm-capital-return-velocity + FinTile on Financials fin-crv); the flagship layered card is profit-model.payback-curve. Financing legitimately speeds the headline (you trade interest, already inside CM, for faster cash); the pre-financing cycle shows alongside as context.",
    mergedFrom: ["financials.capital-return-velocity"],
  },
  {
    id: "profit-model.profit-inflection-point",
    title: "Profit inflection point",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "dashboard", page: "Profit model (Exec)", renderId: "pm-profit-inflection-point" },
      { pageKey: "financials", page: "Financials", renderId: "fin-pip" },
    ],
    definition: "The monthly revenue you need to cover overhead and your current acquisition spend. Below it the month loses money, and above it each extra dollar starts adding profit.",
    arithmetic: "(Today's monthly overhead + average monthly acquisition spend from the three most recent complete sales months) ÷ the net Contribution Margin rate from those same three months. Revenue has sales tax removed while shipping stays in. When a store supplies one whole-order refund total, the calculation subtracts only the part that belongs to those sales dollars. Profit days = 30 × (1 − break-even revenue ÷ current monthly revenue), an even-sales picture of the cushion rather than a count of actual calendar days.",
    example: "Vital Roots: about $137,428 per month to break even. At about $202,982 in net monthly revenue, that is roughly 48% above break-even.",
    windowBehavior: "A per-month run-rate by design — built from your recent complete months, so the date picker doesn't stretch or shrink it.",
    metricIds: ["cm_amount", "cac"],
    note: "This is blended, so a product filter will not move it. It needs your overhead and costs entered. Raising acquisition spend raises the bar. Profit days assume sales arrive evenly, so they are an easy picture of the cushion, not actual profitable calendar days. This is monthly break-even revenue, different from the customer payback day.",
    mergedFrom: ["financials.profit-inflection-point"],
  },
  {
    id: "financials.operating-profit",
    title: "Operating profit",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "financials", page: "Financials", renderId: "fin-operating-profit" },
    ],
    definition: "What you actually earned over the dates you picked, once every cost is out. That means the costs that move with each sale, your marketing, and your fixed overhead. This is the real bottom line.",
    arithmetic: "Window contribution margin, after subtracting the part of each refund that belongs to those sales − window marketing spend (ads + affiliate commissions) − overhead totaled across the window (on 'All time', across your real data span so it fills in instead of showing a dash). This is profit, so the sidebar's gross view never applies here.",
    example: "Vital Roots, year-to-date (≈6 months): $611,997 margin − $278,380 marketing − $132,000 overhead ≈ $201,617 operating profit.",
    windowBehavior: "Follows the timeline you pick, including 'All time' — there overhead is totaled across your real data span (the months you actually have orders for), so it shows a real number instead of a dash. Needs costs, spend AND overhead entered — a profit number missing a cost isn't a profit number.",
    metricIds: ["cm_amount", "cac"],
    note: "This is blended, so a product filter won't move it. It needs costs, marketing, and overhead. If only one or two overhead months are recorded, the card stays visible but warns that earlier months use the earliest known amount and are still estimates. It is dated by when orders came in, not when the cash hit your bank.",
  },
  {
    id: "financials.roic",
    title: "Capital return multiple",
    titleSuffix: "12-month run rate",
    placements: [
      { pageKey: "financials", page: "Financials", renderId: "fin-roic" },
    ],
    definition: "A planning estimate of how much revenue or Contribution Margin each $1 of growth capital could generate as that dollar comes back and gets used again. It is not full Return on Invested Capital.",
    arithmetic: "One turn = first-year value per customer ÷ the cost to get and serve that customer. The cost is acquisition cost plus overhead per customer. Turns per year = 12 ÷ Capital Return Velocity months. Revenue uses first-year revenue. CM uses first-year Contribution Margin. A whole-order refund subtracts only the part that belongs to the selected value. The capital-recycled dial controls how much of the original dollar stays in the next turn. The CAC-increase dial raises the cost of later customers as volume grows.",
    example: "$300 of first-year revenue ÷ $125 to get and serve a customer = $2.40 generated for each $1 in one turn. If the same dollar turns four times and 100% stays in the model, it generates $9.60 of first-year revenue. CM shows the same estimate using margin instead of revenue.",
    windowBehavior: "Uses a fixed 365-day customer value, so the headline does not jump when you change the date window. Capital turns and the cost to get and serve a customer come from the selected window. Both planning dials stay saved until you change them. It needs product costs, marketing spend, overhead, and inventory lead time. If any are missing, it shows what is needed instead of a sample number.",
    metricIds: ["roic", "payback_cost_floor"],
    note: "This is a planning estimate, not true Return on Invested Capital. A full calculation would need taxes, debt, working capital, and the full amount invested in the company. Revenue shows top-line output. CM is closer to economic return, but it is still not net operating profit. It is blended across products and cannot see one-time surprises.",
    opsNote: "Founder decision 2026-07-16: renamed Capital return multiple and rebuilt as an honest planning estimate, not GAAP-style ROIC. It sums each turn's fixed 365-day Revenue or CM value per customer over the capital turns, with a recovered-capital recycle dial and a CAC-growth assumption. It is in Needs review and stays hidden from real clients until approved.",
  },
  {
    id: "financials.operating-profit-by-month",
    title: "Operating profit by month",
    placements: [
      { pageKey: "financials", page: "Financials", renderId: "fin-op-profit-trend" },
    ],
    definition: "Operating profit drawn month by month, so you see which months actually made money. Loss months show as red bars, because hiding them would hide the story.",
    arithmetic: "Per complete month: contribution margin, after subtracting the part of each refund that belongs to those sales − that month's marketing spend (ads + commissions) − the overhead that was ACTUALLY in effect that month (from your effective-dated overhead history — a month before you raised overhead uses the lower figure, not today's). Months before your first recorded overhead fall back to your earliest known figure.",
    example: "Vital Roots, a typical month: $102K CM − $46K marketing − $22K overhead = $34K operating profit (a month when overhead was still $18K would use $18K, not today's figure).",
    windowBehavior: "Shows ALL complete months of your history by design (first order month → now), not just the picked window. The in-progress month is left out — it would read falsely low. Each month carries its own era's overhead.",
    metricIds: ["cm_amount"],
    note: "This is blended, so a product filter won't move it. It shows finished months only. If overhead history is thin, the card warns that earlier months use the earliest known amount and are still estimates. It is dated by when orders came in, not when the cash hit your bank.",
  },
  {
    id: "financials.revenue-vs-breakeven",
    title: "Revenue vs true break-even, by month",
    placements: [
      { pageKey: "financials", page: "Financials", renderId: "fin-rev-vs-breakeven" },
    ],
    definition: "Each month's revenue (the bars) next to the revenue that month NEEDED to break even (the line), so a bar above the line really is a profit month.",
    arithmetic: "Break-even line per month = (that month's ACTUAL overhead + that month's marketing spend) ÷ that month's margin %. A whole-order refund subtracts only the part that belongs to the sales dollars used by margin. The overhead comes from your effective-dated history, so a historical month uses the overhead of its era, not today's; months before your first record fall back to the earliest known figure. The line MOVES when you spend more — a heavy testing month raises the bar it has to clear.",
    example: "Vital Roots: a month with $207,720 net revenue vs $161,270 needed cleared its bar by about 29%.",
    windowBehavior: "Shows ALL complete months of your history by design, not just the picked window. A month with zero or negative margin can't have a break-even line and is skipped with a note. Each month's bar uses its own era's overhead.",
    metricIds: ["revenue", "cm_amount"],
    note: "This is blended, so a product filter won't move it. The break-even line moves when you spend more. If overhead history is thin, the card warns that earlier months use the earliest known amount and are still estimates.",
  },
  {
    id: "financials.reinvestment-capacity",
    title: "Reinvestment capacity",
    placements: [
      { pageKey: "financials", page: "Financials", renderId: "fin-reinvest" },
    ],
    definition: "The honest version of 'how much more can I spend?', the profit your operation frees up per month, paired with how long a reinvested dollar stays gone.",
    arithmetic: "Average operating profit (net margin − marketing − overhead) over the last 3 complete months; the return time shown next to it is your Capital Return Velocity. A whole-order refund subtracts only the part that belongs to the sales dollars margin counts.",
    example: "Vital Roots: ≈ $31,000/month freed; an extra acquisition dollar comes back in ~6 months.",
    windowBehavior: "Uses the last 3 complete months by design, not the picked window. We never see your bank balance — this is what operations generate, the ceiling on self-funded growth.",
    metricIds: ["cm_amount"],
    note: "This is a blended average, so a product filter won't move it. We don't see your bank balance on purpose, so there are no fake runway numbers here. This is the cash your operations throw off, which is the ceiling on the growth you can fund yourself.",
    opsNote: "Kept for saved layouts and parked on Later. The separate Capital return multiple is also parked until its planning estimate is founder-approved.",
  },
  {
    id: "marketing.amer",
    title: "aMER (blended)",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "amer-acquisition" },
    ],
    definition: "For every dollar you spend to get a new customer, what their first order brings back. Flip between Revenue and CM on the card. This is the honest acquisition read before repeat orders pad it. Blended across all traffic channels, with channel detail in the drill.",
    arithmetic: "Revenue from each customer's first-ever order (landing in the window) ÷ acquisition spend — ad spend plus affiliate commissions on FIRST orders only. CM uses those same first orders after variable costs. A whole-order refund subtracts only the part that belongs to the sales dollars CM counts. Commissions on repeat orders are a cost of keeping customers, counted in MER and the profit model instead, never here.",
    example: "Vital Roots, year-to-date: $479,451 first-order revenue ÷ $273,304 acquisition spend = 1.75×.",
    windowBehavior: "Follows the timeline you pick. Blended on spend — acquisition spend isn't tracked per product.",
    metricIds: ["mer", "new_customers"],
    mergedFrom: ["marketing.cm-amer", "marketing.breakeven-cm-roas"],
    note: "New acquisition only. Affiliate commissions on repeat orders are left out because they are a retention cost. In CM, 1.0× is break-even. In Revenue, the matching break-even target depends on your current margin and appears below the number. CM needs your product costs built.",
    opsNote: "Blended — every channel and product in one figure; the per-channel version is its own card. A by-first-product split is planned once per-order commission plumbing lands.",
  },
  {
    id: "marketing.marketing-spend",
    title: "Marketing spend",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "marketing-spend" },
    ],
    definition: "The marketing money you spent over the dates you picked, your entered ad spend plus tracked affiliate commissions, blended across all traffic channels. Drill in to view by channel.",
    arithmetic: "Monthly ad spend prorated to the part of each month inside the window + affiliate commissions on the window's affiliate orders.",
    example: "Vital Roots, year-to-date: $265,404 ad spend + $12,976 commissions = $278,380.",
    windowBehavior: "Follows the timeline you pick. Monthly ad spend is spread evenly, so odd windows prorate it. Blended — spend isn't tracked per product.",
    metricIds: ["cac"],
    note: "Your ad spend is what you enter in Settings, and commissions come from your affiliate platform. Anything you haven't set up isn't counted, so this is only the spend the dashboard knows about.",
  },
  {
    id: "marketing.blended-cac",
    title: "Blended CAC",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "blended-cac" },
    ],
    definition: "The cost to get one brand-new customer, shown as a blended average across all traffic channels. Drill in to view by channel.",
    arithmetic: "(ad spend + affiliate commissions on customers' FIRST orders) ÷ new customers. Commissions on repeat orders are a retention cost, not acquisition, so they're left out.",
    example: "Vital Roots, year-to-date: $273,304 acquisition spend ÷ 5,065 = $53.96.",
    windowBehavior: "Follows the timeline you pick. Blended — spend isn't tracked per product. Short windows read high.",
    metricIds: ["cac"],
    note: "This measures new-acquisition only. Affiliate commissions on repeat orders are left out, because they're a cost of KEEPING customers, so they land in your whole-business totals like Marketing Efficiency Ratio (MER) and Your Profit Model instead.",
  },
  {
    id: "marketing.ltv-cac-gross",
    title: "LTV : CAC",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "ltv-cac" },
      { pageKey: "dashboard", page: "Profit model (Exec)", renderId: "pm-ltv-cac" },
    ],
    definition: "Lifetime Value (LTV) to Customer Acquisition Cost (CAC): what a fully matured customer group became worth versus what it cost to acquire those exact people. Flip between Revenue and CM on the card. A 3-to-1 ratio is the usual floor, and higher is better.",
    arithmetic: "Choose one fully completed customer group for the selected horizon. Divide that group's average Revenue or Contribution Margin LTV by the acquisition cost for those exact same customers. A whole-order refund subtracts only the part that belongs to the selected LTV dollars. Revenue / CM changes only the LTV dollars. The people and CAC stay identical.",
    example: "$49.01 of 90-day Contribution Margin LTV ÷ $18.44 to acquire those same customers = 2.66:1.",
    windowBehavior: "The picker chooses the measurement horizon. A 90-day pick uses the newest customer group that has fully completed 90 days, then measures those customers through day 90 and uses acquisition costs from their acquisition dates. A custom range keeps the acquisition dates you chose and hides any horizon that group has not fully completed. This CAC can differ from Blended CAC, which follows the literal picker dates.",
    metricIds: ["ltv", "cac"],
    note: "Blended, so a product filter will not move it. The small customer group behind LTV and CAC is identical. CM needs your product costs built. A much lower CM result is the honest signal a healthy-looking Revenue ratio can hide.",
    mergedFrom: ["marketing.cm-ltv-cac", "profit-model.ltv-cac"],
  },
  // marketing.cac-by-channel + marketing.amer-by-channel REMOVED as standalone
  // cards (founder 2026-07-14): "View by channel" now opens the by-channel
  // breakdown as a real drill-down modal (NodeDrill), not an inline card. They're
  // drill VIEWS of the core CAC / aMER cards now, not catalog entries. The old
  // day1-cm-roas-by-channel merge is dropped with them (it was CM-mode of aMER-by-channel).
  {
    id: "marketing.revenue-by-channel",
    title: "Revenue by channel",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "mkt-acquisition" },
    ],
    definition: "Your Revenue or contribution margin (CM) split by where it came from: email, organic, paid social and search, affiliate, direct, and unknown. Click any slice to dig in.",
    arithmetic: "Group the same selected orders by attributed channel. Revenue uses the selected revenue basis. CM uses those same orders and subtracts each SKU's variable costs. A whole-order refund subtracts only the part that belongs to the sales dollars the selected view counts.",
    example: "Vital Roots, year-to-date: email $380,546, organic $256,331, meta $204,832, direct $96,230, google $94,502, affiliate $85,033.",
    windowBehavior: "Follows the timeline you pick. Product-scopable in the drill.",
    metricIds: ["revenue", "cm_amount"],
    note: "This includes every channel, not just paid media. Revenue / CM changes only the dollar basis. Pick a product in the drill to narrow it. CM turns on after every selling product has costs.",
  },
  {
    id: "marketing.revenue-over-time",
    title: "Revenue over time",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "mkt-demand" },
    ],
    definition: "Your Revenue or contribution margin (CM) over the dates you picked, how it changed versus the period before, and a month-by-month line.",
    arithmetic: "Revenue = selected order money after the chosen revenue adjustments. CM = those exact orders' per-SKU contribution margin. A whole-order refund subtracts only the part that belongs to the sales dollars the selected view counts. The comparison and monthly line use the same selected basis.",
    example: "Vital Roots, year-to-date: Revenue = $1,117,474 and CM = $611,997 after variable costs.",
    windowBehavior: "Follows the timeline you pick. Fully product-scopable in the drill.",
    metricIds: ["revenue", "cm_amount"],
    note: "Revenue is net by default, so it reads lower than the gross Revenue basis Marketing Efficiency Ratio uses. CM turns on after every selling product has costs. The drill follows the same Revenue or CM choice.",
  },
  {
    id: "marketing.new-customers",
    title: "New customers",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "new-customers" },
    ],
    definition: "How many brand-new customers you got over the dates you picked, with your total customer count underneath.",
    arithmetic: "Distinct customers whose first-ever order lands in the window; the sub-line is everyone (distinct) who ordered in the window, new or returning. With a product selected, the first-ever order must include that product.",
    example: "Vital Roots, year-to-date: 5,065 new (7,342 total) — the same 5,065 that Blended CAC divides by.",
    windowBehavior: "Follows the timeline you pick.",
    metricIds: ["new_customers"],
    note: "With no product selected, this is the exact count your Blended Customer Acquisition Cost (CAC) divides by. A product selection narrows this card, but not Blended CAC. For a selected product, 'new' means the customer's first-ever order included it. New plus returning still add up to your total.",
  },
  {
    id: "marketing.aov",
    title: "Avg order value",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "avg-order-value" },
    ],
    definition: "The average Revenue or contribution margin (CM) created by each order over the dates you picked.",
    arithmetic: "Revenue view = net Revenue divided by order count. CM view = contribution margin from those exact same orders divided by that same order count. A whole-order refund subtracts only the part that belongs to the sales dollars the selected view counts. The headline, chart, and drill all change together.",
    example: "Vital Roots, year-to-date: $1,117,474 ÷ 15,018 = $74.41 of Revenue per order. The CM view uses those same 15,018 orders.",
    windowBehavior: "Follows the timeline you pick. Product-scopable in the drill.",
    metricIds: ["aov", "cm_aov"],
    note: "Revenue and CM use the same order population. Open the drill to see the selected basis by month, product, state, or new versus returning.",
  },
  {
    id: "marketing.revenue-by-product",
    title: "Revenue by product",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "mkt-demand" },
    ],
    definition: "Which products drive your Revenue or contribution margin (CM), showing the top 3 with a choice between products and SKUs. Click for the full ranking.",
    arithmetic: "Group the selected item rows by product family or SKU. Revenue = price charged × quantity. CM = that same item's stored per-SKU contribution margin. Revenue / CM changes only the dollar basis and ranking.",
    example: "Vital Roots, year-to-date: Daily Greens $420,163, Deep Sleep $244,802, Radiant Collagen $128,458.",
    windowBehavior: "Follows the timeline you pick. Item-level basis, so it won't sum to the order-level Revenue card.",
    metricIds: ["revenue", "cm_amount"],
    note: "Revenue counts each item's own sales, so it can differ from the whole-order Revenue card. CM turns on after every selling product has costs. The Product / SKU choice is separate and never changes the Units sold card.",
  },
  // marketing.units-sold MERGED into products.total-units (founder 2026-07-15):
  // identical metric (true units, `boxes`), so it's now ONE "Units sold" card with
  // a Products + a Marketing placement. "Units sold per product" stays separate.
  {
    id: "marketing.customer-segments-rfm",
    title: "Customer segments (RFM)",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "mkt-attribution-segments" },
    ],
    definition: "Your customers grouped by how recently and how often they buy and how much they spend — Champions, Loyal, Promising, and Dormant — with each group's plain-language definition under its name. The win-back 'Overdue customers' row sits in the same list: repeat buyers now past their own usual reorder rhythm.",
    arithmetic: "Per customer over all-time: Champions bought in the last 90 days with 3+ orders; Loyal within a year with 2+; Promising within a year with 1; Dormant otherwise. The Overdue row = customers with 2+ orders in the past year who have now gone past 1.5× their OWN average gap between orders (each measured against their own cadence, never a blended one).",
    example: "Vital Roots: Champions 1,889, Loyal 3,737, Promising 7,890, Dormant 8,835; plus 3,033 overdue customers with about $1,241,348 of spend at risk.",
    windowBehavior: "ALL-TIME on purpose — segments describe who a customer IS today, and an overdue read needs full cadence history, so the date picker doesn't apply and these counts won't match the window's customer cards. Does follow the sidebar product/source filter (the segments of customers acquired via it).",
    metricIds: [],
    note: "This is a lifetime view, so it ignores the date picker and won't match your window's customer counts. Pick a product to see the segments for customers who bought it. The Overdue row is based on each customer's own habit, so it can overlap Loyal and Champions (both are repeat buyers); it can never overlap Promising, which is a one-time buyer.",
    opsNote: "Merged 2026-07-13: 'Overdue customers' is a win-back ROW inside this one card (RfmCard), not a separate card — one approval covers both (absorbed marketing.overdue-customers). Renders under the mkt-attribution-segments section, so its live ⓘ is component-sourced (the segments and the overdue row each have their own popover).",
    mergedFrom: ["marketing.overdue-customers"],
  },
  {
    id: "marketing.attribution-variance",
    title: "Is your platform over-reporting?",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "mkt-attribution-segments" },
    ],
    definition: "What each ad/affiliate platform CLAIMS it drove versus the dollars you actually COLLECTED from matched orders — over-reporting made visible, with a match rate.",
    arithmetic: "(Claimed revenue − collected revenue) ÷ collected revenue, plus the match rate. Both sides are product dollars before shipping and tax. Rejected conversions stay out, and an order matched by two conversions counts once. When collected is $0, variance is unavailable because division by zero has no honest percentage.",
    example: "Read the headline: claimed vs collected, the variance %, and the match rate. A low match rate signals tracking problems, not over-spending.",
    windowBehavior: "Follows the timeline, scoped by the CONVERSION date on both sides so claimed and collected share one window. Blended — not product-scopable; locked until an ad/affiliate platform is connected.",
    metricIds: [],
    note: "A low match rate points to tracking problems, not over-spending. Months with no matched collected dollars show as unmatched and break the variance line, never as a fake 0%.",
  },
  {
    id: "marketing.geography",
    title: "Geography",
    placements: [
      { pageKey: "marketing", page: "Marketing", renderId: "mkt-geo" },
    ],
    definition: "Where your customers, Revenue, and contribution margin (CM) are, on a US map and broken out by state.",
    arithmetic: "Group the selected orders by each customer's home state from their first order. Revenue uses the selected revenue basis. CM uses those same orders and subtracts each SKU's variable costs. A whole-order refund subtracts only the part that belongs to the sales dollars the selected view counts. Customer count and value per customer use the same state groups.",
    example: "Vital Roots: revenue (net of refunds), customers and value-per-customer by US state for the window.",
    windowBehavior: "Follows the timeline you pick. Narrows with the product filter (order-level — orders containing the product). US states only; customers whose state can't be mapped are counted separately, not guessed.",
    metricIds: ["revenue", "cm_amount"],
    note: "Pick a product to narrow it. Revenue / CM changes only the money basis. US states only; anyone we cannot place is counted separately, never guessed. We keep the state, never the full address.",
  },
  {
    id: "products.products-sold",
    title: "Products sold",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "products", page: "Products", renderId: "products-sold" },
    ],
    definition: "How many products your customers actually bought. A multi-pack counts as one, and free $0 giveaway items don't count.",
    arithmetic: "Sum each SKU's paid sale-units (lines where the price charged was above $0).",
    example: "Vital Roots, year-to-date: 17,262 products sold (the free 7-Day Guide counts as 0 even though it's on 1,588 orders).",
    windowBehavior: "Follows the timeline you pick. Product-scopable via the right-rail filter.",
    metricIds: ["units"],
    note: "Free $0 bonus items are left out on purpose, even when they ride along on paid orders.",
  },
  {
    id: "products.total-units",
    title: "Units sold",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "products", page: "Products", renderId: "total-units" },
      { pageKey: "marketing", page: "Marketing", renderId: "mkt-demand" },
    ],
    definition: "The individual units that went out the door. A 6-pack counts as 6, and a separate sample discounted to $0 still counts because it physically shipped. Products sold counts the paid pack as one and leaves the sample out.",
    arithmetic: "For each real catalog line, multiply the order quantity by the pack size and add the results. A positive-price sample discounted to $0 stays in. Literal $0 rows that only list a pack's component units stay out so the same bottles are never counted twice.",
    example: "Year-to-date: 24,348 individual units (Daily Greens 90ct sells in 3-packs, so 2,476 sales = 7,428 units).",
    windowBehavior: "Follows the timeline you pick. Product-scopable.",
    metricIds: ["boxes"],
    note: "Pick a product to narrow this. A pack only counts as more than one after you tell us what's inside it. A separate physical sample counts, even when it is free. A $0 bookkeeping row for units already inside a paid pack does not count again. Switch to By product for the split.",
    mergedFrom: ["marketing.units-sold"],
  },
  {
    id: "products.product-revenue-over-time",
    title: "Product revenue over time",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "products", page: "Products", renderId: "product-revenue-trend" },
    ],
    definition: "Your product Revenue or contribution margin (CM) month by month, on the same basis as the Product details table.",
    arithmetic: "Group paid product lines by order month. Revenue = price charged × quantity, excluding shipping and tax and before refunds. CM adds those same lines' stored per-SKU contribution margin. Revenue / CM changes only the dollar basis.",
    example: "The monthly points add up to the same selected-period total as the Product details table.",
    windowBehavior: "Follows the timeline you pick. Narrows with the product filter. Month, product, and SKU views all use the same catalog inclusion rules.",
    metricIds: ["product_revenue", "product_cm"],
    note: "Pick a product to narrow both views. Product Revenue excludes shipping and tax and stays before refunds so it matches the table. CM turns on after every selling product has costs.",
    opsNote: "Founder 2026-07-15 (#7): now rendered on the default Products page (ProductRevenueTrendCard, Hideable id product-revenue-trend) and its anchor is emitted, so 'See it live' spotlights it.",
  },
  {
    id: "products.revenue-concentration",
    title: "How concentrated is revenue?",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "products", page: "Products", renderId: "revenue-concentration" },
    ],
    definition: "Each product's share of Revenue or contribution margin (CM). It spots when your sales or margin lean too hard on one hero product.",
    arithmetic: "Item-level Revenue or per-SKU CM for each product or SKU ÷ the matching total, ranked biggest first. Revenue / CM changes only the dollar basis.",
    example: "Vital Roots, year-to-date: Daily Greens ≈37% of the $1.19M item-level total — the dependency signal.",
    windowBehavior: "Follows the timeline you pick. Product-scopable. Toggle SKU ↔ Product.",
    metricIds: ["revenue", "cm_amount"],
    note: "Pick a product to narrow it. It uses each item's own Revenue or CM, so it lines up with the product table. Product / SKU changes the grouping; Revenue / CM changes the dollar basis.",
  },
  {
    id: "products.units-per-product",
    title: "Units sold per product",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "products", page: "Products", renderId: "units-per-product" },
    ],
    definition: "Each product's share of individual units sold, your volume leaders, which aren't always your revenue leaders.",
    arithmetic: "Individual units (pack-adjusted) per product or SKU, as a share, biggest first.",
    example: "Vital Roots, year-to-date: Daily Greens leads on units at 9,814 (bigger in unit-share than revenue-share, thanks to its 3-packs).",
    windowBehavior: "Follows the timeline you pick. Product-scopable.",
    metricIds: ["boxes"],
    note: "It counts individual units in both views, so the toggle never changes the basis and it matches the Units sold tile. Separate physical samples count. $0 bookkeeping rows for units already inside a pack do not count again.",
  },
  {
    id: "products.product-table",
    title: "Product details",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "products", page: "Products", renderId: "product-table" },
    ],
    definition: "Your full catalog as a sortable, searchable table, with price, units, Revenue, contribution margin (CM), orders, and last-sold for every product. Click a product to expand its SKUs.",
    arithmetic: "Per product or SKU: units, Revenue, CM, order count, and last-sold inside the window from actual order lines. Price is the most recent price charged per sale-unit. A 3-pack's price is the pack price, not one bottle. CM comes from per-SKU cost inputs and stays blank until costs are built.",
    example: "Vital Roots, year-to-date: Daily Greens → GRN-90 (a 3-pack) at $129 per pack, 2,476 packs sold, $319,404 (2,476 × $129 = $319,404 exactly).",
    windowBehavior: "Follows the timeline you pick. Product-scopable via search + rail.",
    metricIds: ["revenue", "units", "cm_amount"],
    note: "Narrow it with search or the product filter. CM shows a dash until you enter that product's costs. For refund rate by product, see Operations.",
  },
  {
    id: "products.movers",
    title: "What's rising & fading",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "products", page: "Products", renderId: "product-movers" },
    ],
    definition: "Which products are rising and fading in Revenue or contribution margin (CM) versus the period before.",
    arithmetic: "For each product, compare item-level Revenue or per-SKU CM in the selected window with the previous same-length window. This uses the same before-refund product dollars as the Product details table. The same products and dates are used in both views.",
    example: "Vital Roots: the biggest up- and down-movers over the window.",
    windowBehavior: "Follows the timeline you pick. Turned off for the 'all' window (there's no prior period to compare).",
    metricIds: ["revenue", "cm_amount"],
    note: "Pick a set date range rather than All time so there is an earlier period to compare. Product Revenue leaves out shipping and tax and stays before refunds, matching the Product details table. CM turns on after every selling product has costs.",
  },
  {
    id: "products.gateway-products",
    title: "Which products create your best customers?",
    placements: [
      { pageKey: "products", page: "Products", renderId: "gateway-products" },
      { pageKey: "cohorts", page: "Customer LTV", renderId: "cv-gateway" },
    ],
    definition: "For each product, how valuable the customers who ever bought it become over their whole life, compared with your average customer. Switch between Revenue and contribution margin (CM).",
    arithmetic: "Take everyone who ever bought the product. Average their all-time Revenue or per-order CM and divide it by the matching average across all customers. A whole-order refund subtracts only the part that belongs to the sales dollars the selected view counts. The people stay identical between views. Products with fewer than 20 buyers are skipped as noise.",
    example: "Vital Roots: 1:1 Wellness Coaching buyers average $315 vs the $186 average customer = 1.69×; Daily Greens 1.39×; Omega Gold 0.86×.",
    windowBehavior: "ALL-TIME (lifetime value) on purpose — ignores the date picker.",
    metricIds: ["ltv", "cm_amount"],
    note: "This is correlation, not proof: your best customers may buy more products because they are already engaged. Revenue / CM changes only the value basis. This is a lifetime view, so the date picker does not change it.",
    opsNote: "It's EVER-bought, not first-bought — the first-product lens lives on the Customer LTV page. One card, two placements (Products gateway-products + Customer LTV cv-gateway).",
  },
  {
    id: "products.bought-together",
    title: "Frequently bought together",
    placements: [
      { pageKey: "products", page: "Products", renderId: "bought-together" },
    ],
    definition: "Which products tend to show up in the same cart, giving you bundle and cross-sell ideas.",
    arithmetic: "Count product-family pairs that appear in the same order, within the window you pick. PAID lines only, since a $0 bonus riding along isn't 'bought together'.",
    example: "Vital Roots: Daily Greens + The Wellness Reset share 1,428 orders (the strongest pairing), then Deep Sleep + The Wellness Reset (1,172).",
    windowBehavior: "Follows the date picker (founder 2026-07-14): counts only orders placed in the window, so recent pairings and newer products surface instead of being buried by years of history.",
    metricIds: [],
    note: "Follows the date picker, so recent and new-product pairings show up. One caveat: a short window can miss a strong seasonal combo just outside it, so widen the range for seasonal bundles. Use it to build bundles and set up cross-sells.",
  },
  // cohorts.headline REMOVED from the review list (founder 2026-07-14, #8): it was
  // never its own tile (no emitted anchor), just the north-star number that already
  // lives at the top of the Customer LTV page. Removing the phantom catalog entry
  // changes nothing a client sees.
  {
    id: "cohorts.ltv-by-first-product",
    title: "Average customer LTV (by product / source / discount / affiliate)",
    placements: [
      { pageKey: "cohorts", page: "Customer LTV", renderId: "cv-breakdown" },
    ],
    definition: "Groups customers by first product, source, discount, or affiliate, then shows average Revenue or contribution margin (CM) at 1, 30, 60, 90, 180, and 365 days.",
    arithmetic: "Group the selected acquisition cohort by the chosen tab. Within each row, find the furthest reliable day and use the customers who reached that day for every value from Day 1 through the last visible day. A whole-order refund subtracts only the part that belongs to the selected LTV dollars. Revenue / CM changes only the dollar basis. The people, dates, and group stay identical.",
    example: "If a row has 1,000 selected customers and 407 have reached its last reliable point at Day 90, Day 1, Day 30, Day 60, and Day 90 all use those same 407 people.",
    windowBehavior: "The window scopes which acquisition cohort is included AND how far out the columns go; a customer who first-bought two products counts in both rows, so rows don't sum to the total.",
    metricIds: ["ltv"],
    note: "The Customers column is everyone in that group. The small n under each dollar is the one fixed mature group used across that row. A young or small row stops at its last reliable day. Total lifetime is every known order to date for the same group, not a prediction. Revenue / CM changes only the dollar basis.",
    opsNote: "One of four TABS in the LTV breakdown card (shared render id cv-breakdown): by product / source / discount / affiliate. The live ⓘ is tab-dependent, so its What/Note stay component-sourced as a documented exception. Fixed-population rule added 2026-07-16 after the old mixed populations made a 180-day Revenue curve fall from $100.69 at Day 60 to $94.82 at Day 90.",
    mergedFrom: ["cohorts.ltv-by-source", "cohorts.ltv-by-discount", "cohorts.ltv-by-affiliate"],
  },
  {
    id: "retention.orders-per-customer",
    title: "Orders per customer",
    placements: [
      { pageKey: "retention", page: "Retention", renderId: "ret-opc" },
    ],
    definition: "How many orders your newly acquired customers place within their first stretch, so you can see whether recent customers are getting more habitual over time.",
    arithmetic: "Use the same fully matured cohort as Repeat rate and average how many orders each customer placed during the selected-length runway. Standard choices use the newest completed group. Custom dates keep the exact acquisition dates you chose. The All view falls back to lifetime orders per customer.",
    example: "Demo: last 90 days = 1.56 orders in the first 90 days; a full year = 2.69; lifetime across everyone = 2.38 (held down by recent buyers who haven't had time to reorder).",
    windowBehavior: "The picker chooses the customer group and runway. A shorter runway usually reads lower because it gives customers less time to reorder. Custom dates keep the exact acquisition dates. The All view is the lifetime average.",
    metricIds: [],
    note: "Pick a product and this keeps only customers whose FIRST order included it. It follows the date picker, so a shorter window reads lower. Based on order counts.",
  },
  // retention.days-to-2nd-order + retention.time-between-orders MERGED (founder
  // 2026-07-14) into exec.time-between-purchases, which now carries the Retention
  // placement (renderId ret-time-between) as its second placement. The single
  // "Days to 2nd order" tile is gone; its number is the merged card's first bar.
  {
    id: "retention.purchase-frequency-funnel",
    title: "Purchase-frequency funnel",
    placements: [
      { pageKey: "retention", page: "Retention", renderId: "ret-funnel" },
    ],
    definition: "Of a group of customers who've all had the same amount of time, how many reached 2, 3, 4, or 5-plus orders, and where they drop off. The steepest drop is your biggest retention leak.",
    arithmetic: "Take the same fully matured cohort as Repeat rate and count how many reached each order depth within the selected-length runway. Standard choices use the newest completed group. Custom dates keep the acquisition dates you chose. Because everyone included has had the full runway, recent customers cannot make the first drop look worse than it is.",
    example: "Vital Roots, 1-year pick: of the 12–24-months-ago cohort, how many reached 2+, 3+, 4+, 5+ orders within their first year.",
    windowBehavior: "The picker chooses the acquisition group and runway. Standard choices use the newest fully completed group. Custom dates keep the exact acquisition dates. 'All time' is the whole business over its lifetime among customers at least 30 days old.",
    metricIds: [],
    note: "Pick a product and this keeps only customers whose FIRST order included it. Choose a 1-year view and it only counts people who've actually had a full year, so the comparison stays fair.",
    opsNote: "Its live ⓘ What/example interpolate the exact cohort shown (data.funnel.cohortNote), so the What stays component-sourced — a documented single-source exception.",
  },
  {
    id: "retention.new-vs-returning",
    title: "New vs returning",
    placements: [
      { pageKey: "retention", page: "Retention", renderId: "ret-nvr" },
    ],
    definition: "Your Customers, Revenue, or contribution margin (CM) split month by month between first-time and returning buyers, with a returning-share line.",
    arithmetic: "Customers count each person once per month: New if their first paid order was that month, otherwise Returning. Revenue and CM tag the first paid order New and every later paid order Returning, including a fast second order in the same month. A whole-order refund subtracts only the part that belongs to the sales dollars the selected view counts. Free replacements do not count as orders, customers, or Revenue, but their real product and shipping costs reduce Returning CM in the month they were sent.",
    example: "Vital Roots, March 2026: 1,018 new + 1,681 returning of 2,699 active = 62.3% returning share.",
    windowBehavior: "Months follow the window, up to the latest 12 inside it. New versus returning is determined from all order history so a pre-window customer is never mislabeled new.",
    metricIds: ["revenue", "cm_amount"],
    note: "Pick a product and this keeps only customers whose first paid order included it. A customer who buys twice in their first month counts once as a New customer, while the second order's Revenue or CM counts as Returning money. Free replacement costs stay in Returning CM without creating a fake order or customer.",
  },
  // retention.retention-by-first-product CUT (founder 2026-07-15): the reorder-cadence
  // and purchase-frequency cards already show per-product behavior via the sidebar
  // product filter (and the funnel's 2nd-order rung IS the per-product repeat rate),
  // so this only added the all-products comparison, which he chose to drop.
  {
    id: "subscriptions.money-actual",
    title: "Subscription money sources",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "sub-money-30d" },
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "subscription-upcoming-charges" },
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "subscription-projection" },
    ],
    definition: "Separately shows actual recurring-order sales, charges scheduled by the named plan provider, and a forecast only when compatible plan and order records reliably match.",
    arithmetic: "Actual sales add recurring-order product revenue billed in the selected window after recorded refunds. Scheduled charges add known next-charge amounts due in the coming 30 days. The forecast uses linked recurring-order history and plan movement only after the source check proves those populations are compatible.",
    example: "A page can show $1,500 from 25 actual recurring orders and, separately, $1,250 from 20 provider-scheduled charges. It does not show a forecast unless the plans reliably match the order history.",
    windowBehavior: "Actual sales follow the timeline you pick. Scheduled charges and any compatible forecast always look forward 30 days.",
    metricIds: ["rebill_revenue"],
    note: "Each live card names its source and keeps actual order sales separate from provider schedules. Contribution margin, product breakdowns, and forecasting stay hidden until their linkage checks pass.",
    mergedFrom: ["subscriptions.money-projected", "subscriptions.money-scheduled"],
  },
  {
    id: "subscriptions.net-adds",
    title: "Net adds",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "net-adds" },
    ],
    definition: "How much the named provider's plan base grew or shrank over the dates you picked.",
    arithmetic: "Plans the named provider recorded as started in the window minus plans it first recorded as ended in the window. A plan with more than one end record counts once, on its first end date.",
    example: "If 120 plans start and 85 end, Net adds = 120 minus 85 = +35.",
    windowBehavior: "Follows the timeline you pick. No pick = the last 30 days. The spark plots new minus ended in each bucket across the same window.",
    metricIds: ["subs_new", "subs_ended"],
    note: "The live card names the plan provider. Product and SKU filters appear only after these plans reliably match imported orders.",
  },
  {
    id: "subscriptions.monthly-churn",
    title: "Churn rate",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "monthly-churn" },
    ],
    definition: "Of the named provider's plans live when your selected window opened, the share that ended through either a customer cancellation or failed payment.",
    arithmetic: "Plans from the provider's opening base whose first recorded end date falls inside the window divided by its plans live when the window opened. Plans that start and end inside the window belong in Net adds, not this rate, so the numerator stays inside the denominator.",
    example: "If 1,000 plans were live when the window opened and 121 of those plans ended, churn = 121 divided by 1,000 = 12.1%.",
    windowBehavior: "Follows the date picker now (it used to be pinned to the last 30 days). No pick = the last 30 days.",
    metricIds: [],
    note: "The live card names the plan provider. A wider window usually shows more total churn but begins with an older, more established base.",
  },
  {
    id: "subscriptions.churn-rate-by-month",
    title: "Churn rate by month",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "churn-rate-by-month" },
    ],
    definition: "The named provider's plan churn month by month, including customer cancellations and failed payments it records.",
    arithmetic: "For each complete month: plans from the provider's opening base that first ended during the month divided by its plans live when the month opened. A plan with more than one end record counts once.",
    example: "A 4% month means 4 of every 100 plans live at the start of that month ended before it closed.",
    windowBehavior: "Always the last 12 data-months — a trend line, so it doesn't follow the date picker (a single window can't show a trend).",
    metricIds: ["subs_ended"],
    note: "The live card names the plan provider. The current month stays hidden until it is complete.",
  },
  {
    id: "subscriptions.active-subscriptions",
    title: "Active subscriptions",
    titleSuffix: "right now",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "active-subs" },
    ],
    definition: "How many plans the named subscription provider marks active right now. It is not presented as a company-wide total when provider coverage is partial.",
    arithmetic: "Count of plans with status active (a subscription = one recurring plan).",
    example: "If the provider reports 500 plans and 120 are active right now, the card shows 120 active and 500 ever.",
    windowBehavior: "A right-now snapshot — it does not follow the date picker.",
    metricIds: [],
    note: "The live card names the plan provider. Product and SKU filters require reliable plan-to-order links.",
  },
  {
    id: "subscriptions.subscription-leaks",
    title: "Subscription leaks",
    titleSuffix: "{timeline}",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "subscription-leaks" },
    ],
    definition: "Why subscriptions ended over the dates you picked, plus the failed-payment money that is still recoverable right now.",
    arithmetic: "Failed payments = plans whose source-recorded failed-rebill disable date falls in the window. Customer cancellations = plans whose cancel date falls in the window. If both times match and the source gives a cancellation reason, it counts as a customer cancellation. Each dollar figure is one normal next rebill for those exact plans. Recoverable now = failed payments still being retried plus plans still disabled after the final retry.",
    example: "Sample data: 31 failed-payment plans represented $7,800 in next-rebill revenue; 43 customer cancellations represented $8,540; $4,900 remained recoverable right now.",
    windowBehavior: "Failed payments and customer cancellations follow the date picker. Recoverable now is intentionally a right-now snapshot.",
    metricIds: [],
    note: "The live card names the plan provider. Product, SKU, and contribution-margin views stay hidden without reliable plan-to-order links.",
    opsNote: "Card-level approval remains required. Providers without source-recorded failure, cancellation, and retry truth must return an honest unavailable state rather than infer a cause from a generic pause.",
  },
  {
    id: "subscriptions.subs-by-type",
    title: "Subscriptions by type",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "subs-by-type" },
    ],
    definition: "Splits your program into physical replenishment versus app subscriptions, because a $9-a-month app plan and a physical plan don't churn or earn alike.",
    arithmetic: "Each plan's type and true start date come from its original sign-up order: software = App, physical = Physical, and untagged or unmatched = Other. Per type: active count, recurring value, total monthly churn from customer cancellations and failed payments, average time to the first end event, and recurring CM using each type's own plans' margins.",
    example: "Vital Roots: Physical 3,009 active / $192,574 recurring / 9.6% churn; App 177 / $1,593 / 10.2% (3,009 + 177 = 3,186).",
    windowBehavior: "A current/point-in-time breakdown — does not follow the date picker.",
    metricIds: [],
    note: "Pick a product to see only its plans. On the CM view, recurring CM uses each plan's own product margin, so the App and Physical rows sum to the base card's Recurring CM.",
  },
  {
    id: "subscriptions.new-vs-canceled",
    title: "New vs ended subscriptions by month",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "subs-new-vs-canceled" },
    ],
    definition: "A month-by-month chart of plans the named provider recorded as started versus first ended.",
    arithmetic: "For each complete month, count provider-recorded plan starts and first endings. A plan with more than one end record counts once. The still-forming month is hidden.",
    example: "301 new versus 97 ended is a net gain of 204 plans for the month.",
    windowBehavior: "The last 12 COMPLETE months, dropping the still-forming current month. Does not follow the date picker.",
    metricIds: ["subs_new", "subs_ended"],
    note: "The live card names the plan provider. The current month stays hidden until it is complete.",
  },
  {
    id: "subscriptions.cancel-curve",
    title: "When exactly do subscribers cancel?",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "cancel-curve" },
    ],
    definition: "Shows when plans in the named provider tend to cancel, measured by week of plan life.",
    arithmetic: "For each week of provider-recorded plan life, cancellations that week divided by plans still active entering that week.",
    example: "Vital Roots: week 5 = 433 cancels ÷ 5,245 at-risk = 8.26% (the biggest spike); also weeks 9 and 18.",
    windowBehavior: "ALL plans, lifetime — does not follow the date picker.",
    metricIds: [],
    note: "The live card names the plan provider. Use the spike weeks to time plan-save or win-back messages.",
  },
  {
    id: "subscriptions.base-lifetime",
    title: "Subscription summary",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "base-lifetime" },
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "subscription-plan-duration" },
    ],
    definition: "How many customers subscribe, what one new subscription is worth after signup, how long mature subscriptions that ended lasted, and how long the money takes to arrive.",
    arithmetic: "% ever subscribed = subscribers ÷ customers. The other three numbers start with plans old enough to complete a full year, measured from each plan's original sign-up order. Average value = first-year rebill revenue or CM after signup ÷ all starts; a whole-order refund subtracts only the part that belongs to the selected value. Average stick rate = full start-to-end time among plans in that group that actually ended, with no 12-month cap. Collection time = the day the full group reaches 90% of its 12-month value. Value and collection time stay blank when fewer than 95% of mature plan totals match the source.",
    example: "Sample data: 23.6% ever subscribed; an average subscription added $342 after signup in its first year; ended plans lasted 3.5 months on average; 90% of the value arrived by month 8.9.",
    windowBehavior: "A fully mature first-year subscription-start cohort. It does not follow the date picker, but it does follow the product and SKU filter.",
    metricIds: [],
    note: "Pick a product or SKU to narrow this card. The sign-up order is left out, and every start stays in the value group, including free trials and people who cancel before the first paid rebill. Each mature plan's imported rebill orders are checked against the source's own rebill total. If 95% to 99.9% of plans match, value and collection time show an estimate mark. Below 95%, they stay blank. The stick-rate line shows the known-end-date population. We use 90% because a few long-running plans can keep adding small amounts for years.",
  },
  {
    id: "subscriptions.cohort-retention",
    title: "Cohort retention: who's still subscribed?",
    placements: [
      { pageKey: "subscriptions", page: "Subscriptions", renderId: "sub-cohort-matrix" },
    ],
    definition: "A grid where each row is a start month in the named plan provider and each cell is the share still active that many months later.",
    arithmetic: "Group plans by the provider-recorded start month. Each cell is the share the provider has not recorded as ended by that point. Leave it blank until the full group has that much history.",
    example: "Vital Roots: 2025-06 cohort M1 = 92% still active, M9 = 63%; newer 2026-03 cohort M1 = 96%.",
    windowBehavior: "ALL plans, cohort grid — does not follow the date picker. Blank cells mean 'not enough time yet', not zero.",
    metricIds: [],
    note: "The live card names the plan provider. A blank cell means the source does not have enough synced history yet, not zero retention.",
  },
  {
    id: "operations.refund-rate",
    title: "Refund rate",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "ops-refunds-returns" },
    ],
    definition: "How often your orders come back as refunds, with the money impact beside it — the dollars refunded, what share of revenue that is, and the average refund size. One headline tile for the whole refund picture.",
    arithmetic: "Refund rate = refunded orders ÷ all orders in the window. Refunded $ = actual cash returned on completed refunds. % of revenue = refund cash ÷ sales before refunds and after sales tax is removed. Avg per refunded order = refund cash ÷ refunded orders.",
    example: "Vital Roots, year-to-date: 681 refunded ÷ 15,018 orders = 4.5%; $40,362.76 refunded; 3.5% of revenue; $59.27 average refund.",
    windowBehavior: "Follows the timeline you pick. Product-scopable; counts whole orders, not dollars. Recent windows can keep rising as late refunds land. For the timing-honest per-product rate, see \"True refund rate by month sold\".",
    metricIds: ["refund_rate", "refunded_amount", "revenue", "refunded_orders"],
    note: "Refund cash can include returned tax or shipping, while dashboard revenue removes sales tax. That makes % of revenue a cash-impact read, not a product-only refund rate. Pick a product and this keeps orders containing it, using the whole order on both sides. Recent windows can rise as late refunds arrive.",
    opsNote: "Consolidated tile (founder 2026-07-13): absorbs the old Refunded $, Refunds % of revenue and Avg refund value tiles as sub-stats on this one card.",
  },
  {
    id: "operations.refunds-by-sale-date",
    title: "True refund rate by month sold",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "ops-refunds-by-sale-date" },
    ],
    definition: "Of the orders you took in each month, the share that eventually came back for a refund, no matter when the refund actually landed. This is the honest per-month return rate. Recent months are faded because their refunds are still coming in.",
    arithmetic: "For each sale month: Orders = refunded orders ÷ that month's orders. Dollars = actual refund cash ÷ that month's sales before refunds and after sales tax is removed. Every later refund stays attached to the original sale month. Months younger than the account's typical order-to-refund lag are marked still settling.",
    example: "Settled cohort, March: 119 of 2,700 March orders were later refunded = 4.4% (Orders view); $6,994 ÷ $196,000 March revenue = 3.6% (Dollars view). The refund stays attached to March even when it happens in May. Sum the settled months and you get the same blended rate the Refund rate tile shows for a fully-mature window.",
    windowBehavior: "Follows the timeline you pick. The picker selects WHICH cohort months show; still-settling months are faded (not final). Follows the sidebar product filter, so choosing a product scopes the monthly bars to that product.",
    metricIds: ["refund_rate_cohort", "refund_rate_cohort_value"],
    note: "Refund cash can include returned tax or shipping, so Dollars is a cash-impact ratio. Faded months are not final yet. Pick a product and this keeps orders containing it, using whole-order dollars on both sides.",
    opsNote: "Founder 2026-07-14 (#2): the old By-product toggle (per-product TOTALS, not by month) was REMOVED — the card is always the monthly cohort view, and the sidebar product filter scopes it to one product's monthly rate. The service still returns byProduct (unused by the UI now).",
  },
  {
    id: "operations.refund-trend",
    title: "Refund money over time",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "ops-refund-trend" },
    ],
    definition: "The refund money leaving each month — the cash-out view, arranged by when the refund actually happened. Pairs with the rate card above (arranged by when the sale happened). \"Show detail\" opens the speed breakdown (how fast refunds came back) and the by-sale-date / by-refund-date / by-product tables.",
    arithmetic: "Bars = completed refunds summed by the REFUND's month (at refund-time exchange rates); line = how many refunds landed that month. Cash-out keeps the full refund. In the by-product detail, one whole-order refund is estimated down to the product share of the original charge, then split across products by their item sales, so shipping and tax stay out of both sides. Refund cash with no usable original sale stays in cash-out but is left out of rate and product math with a visible warning. If the provider hasn't supplied true refund dates yet, timing waits instead of guessing.",
    example: "Vital Roots: a month's bar mixes refunds on that month's own sales with refunds on sales from earlier months — so it won't match the by-sale-date rate card month for month. On an all-history window both axes converge on the one $40,362.76 total.",
    windowBehavior: "Follows the timeline you pick. On the REFUND-date axis. The detail tables each use their stated date axis, so the axes won't match month-for-month inside a window, by design.",
    metricIds: ["refunded_amount", "refunded_orders"],
    note: "Pick a product to narrow this. It's arranged by REFUND date, so it won't line up month-for-month with the by-sale-date card above. Cash-out always keeps the full refund. Product detail removes estimated shipping and tax so it compares product refund dollars with product sales. Any refund missing an original sale is called out and kept out of rate math.",
    opsNote: "Consolidated (founder 2026-07-13): merges the old \"Refunds: dollars & rate over time\" combo and \"Refunds by refund date\" into one money-over-time card, with the Refund-detail tables (avg value/rate over time, by sale/refund date, by product) as its Show-detail drill.",
  },
  {
    id: "operations.chargeback-rate",
    title: "Chargeback rate",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "chargeback-rate" },
    ],
    definition: "The share of orders that turned into a chargeback (a customer disputing the charge with their bank). High dispute rates can trigger processor or card-network monitoring.",
    arithmetic: "Of the orders PLACED in the window, those with ANY dispute ÷ all the window's orders — the same order population on both sides. Won disputes still count because winning the money back does not erase the dispute.",
    example: "60 disputed orders ÷ 15,000 orders = 0.4%.",
    windowBehavior: "Follows the timeline you pick. Counts whole orders, not dollars; order-date axis on both sides. This shows which sales later became disputes. Formal card-network monitoring uses different month/payment rules, so this is not a processor-risk threshold calculator.",
    metricIds: ["chargeback_rate"],
    note: "Pick a product to narrow this. It counts ORDERS, not dollars, so it won't match 'Chargebacks % of revenue.' Click in to slice by month, product, or channel.",
  },
  {
    id: "operations.chargeback-amount",
    title: "Chargebacks $",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "chargebacks" },
    ],
    definition: "Confirmed chargeback losses plus the money still at risk in open disputes, before any extra processor fees.",
    arithmetic: "Sum of open + lost dispute amounts on the window's orders, converted at the dispute-open exchange rate (won disputes excluded — the money came back; an open dispute may still be won).",
    example: "$3,900 across 60 disputed orders.",
    windowBehavior: "Follows the timeline you pick. Split by product, each order's dispute is spread across its items by revenue share — never double-counted.",
    metricIds: ["chargeback_amount"],
    note: "Pick a product to narrow this. Won disputes are excluded because that money came back. When you split by product, each dispute is spread across the items in that order, never double-counted. Disputes that are not linked to an order are disclosed separately and excluded until matched.",
  },
  {
    id: "operations.chargebacks-pct-revenue",
    title: "Chargebacks % of revenue",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "chargebacks-of-revenue" },
    ],
    definition: "Lost and still-open dispute dollars as a share of revenue, showing how much cash is lost or at risk.",
    arithmetic: "Open and lost dispute dollars ÷ sales before refunds and after sales tax is removed, over the same order dates.",
    example: "$3,900 ÷ $1,157,837 = 0.3%.",
    windowBehavior: "Follows the timeline you pick. A dollar ratio, unlike the order-count chargeback rate — the two won't match.",
    metricIds: ["chargeback_amount", "revenue"],
    note: "A dispute can include tax or shipping, while dashboard revenue removes sales tax. That makes this a cash-impact read. Pick a product and this keeps orders containing it, using the whole order on both sides. Chargeback rate counts orders, so the two will not match.",
  },
  {
    id: "operations.chargeback-count",
    title: "Chargeback count",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "chargeback-count" },
    ],
    definition: "How many of the window's orders were disputed — the raw count behind the rate.",
    arithmetic: "Distinct orders placed in the window that carry any dispute, including disputes you later won.",
    example: "60 disputed orders.",
    windowBehavior: "Follows the timeline you pick.",
    metricIds: ["chargeback_count"],
    note: "Pick a product to narrow this. It counts whole orders, including disputes you later won. Disputes that are not linked to an order are disclosed separately and excluded until matched. Click in to slice by month, product, or channel.",
  },
  {
    id: "operations.overhead-pct-revenue",
    title: "Overhead % of revenue",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "overhead-of-revenue" },
    ],
    definition: "Overhead as a share of revenue — the raw weight of fixed costs on the top line.",
    arithmetic: "For each point: total effective-dated overhead across the trailing three complete months ÷ total revenue from those same three months. The still-forming month is left out.",
    example: "Vital Roots: $101,136 of overhead ÷ $632,098 of revenue across the same three complete months = 16.0%.",
    windowBehavior: "A per-month run-rate — it does NOT grow or shrink with the window you pick (by design).",
    metricIds: [],
    note: "This is blended, so a product filter won't move it. If overhead history is thin, the card warns that earlier points use the earliest known amount and are still estimates.",
  },
  {
    id: "operations.overhead-per-customer",
    title: "Overhead per customer",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "overhead-per-customer" },
    ],
    definition: "Monthly overhead spread across the customers you served — a falling number as you grow means operating leverage is working. Spread over ALL buyers, never just new ones.",
    arithmetic: "Monthly overhead ÷ average monthly distinct customers, over the recent COMPLETE months matching the window (the still-forming current month is left out, so a half-month of customers can't make it read ~2× high; 30 days ≈ 1 month, 90 ≈ 3, a year ≈ 12).",
    example: "Vital Roots: $22,000 ÷ 2,311.7 avg monthly customers = $9.52.",
    windowBehavior: "Moves with the picker via the number of months averaged; built on a monthly basis (never window÷30) so an 'all' window can't blow it up ~30×.",
    metricIds: [],
    note: "This is blended, so a product filter won't move it. The headline uses today's overhead with the average monthly customer count for the chosen window. The trend uses each month's recorded overhead. Thin history triggers an estimate warning.",
  },
  {
    id: "operations.labor-pct-revenue",
    title: "Labor % of revenue",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "ops-labor-pct-revenue" },
    ],
    definition: "Labor cost as a share of revenue — locked until labor is split out from overhead.",
    arithmetic: "Labor ÷ revenue (once labor detail exists).",
    example: "No value — shows a lock with 'Add labor detail to your costs to unlock.'",
    windowBehavior: "Locked by design — no value computed yet.",
    metricIds: [],
    note: "Locked for now. Your overhead is a single number today, so you'll first need to split labor out of it, whether you type it in, upload a P&L, or connect your accounting.",
  },
  {
    id: "operations.labor-pct-cm",
    title: "Labor % of CM",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "ops-labor-pct-cm" },
    ],
    definition: "Labor's share of contribution margin — locked until labor is split out from overhead.",
    arithmetic: "Labor ÷ contribution margin (once labor detail exists).",
    example: "No value — shows a lock with the unlock reason.",
    windowBehavior: "Locked by design — no value computed yet.",
    metricIds: [],
    note: "Locked for now. You'll first need to split labor out of your single overhead number, whether you type it in, upload a P&L, or connect your accounting.",
  },
  {
    id: "operations.operating-leverage",
    title: "Operating leverage",
    placements: [
      { pageKey: "operations", page: "Operations", renderId: "ops-operating-leverage" },
    ],
    definition: "How fast fixed costs rise per $1M of new revenue — locked until we have a couple of overhead snapshots at different revenue levels.",
    arithmetic: "Change in fixed costs per $1M revenue added (accumulated over time).",
    example: "No value — shows a lock with the unlock reason.",
    windowBehavior: "Locked by design — no value computed yet.",
    metricIds: [],
    note: "Locked for now. It needs a couple of overhead readings at different revenue levels, which build up as we track your overhead over time.",
  },
];

// ---------------------------------------------------------------------------
// The ship set (grandfather list) — the merged canonical ids present the day the
// merge shipped. Listed EXPLICITLY so a card added later is NOT grandfathered.
// ---------------------------------------------------------------------------

export const SHIP_SET_CARD_IDS: readonly string[] = [
  "exec.revenue",
  "exec.orders",
  "exec.customers",
  "exec.repeat-rate",
  "exec.refund-rate",
  "exec.mer",
  "exec.acquisition-cac",
  "exec.operational-drag",
  "exec.top-products",
  "exec.revenue-by-month",
  "exec.time-between-purchases",
  "exec.profit-equation",
  "profit-model.contribution-margin",
  "profit-model.acquisition-economics",
  "profit-model.operational-efficiency",
  "profit-model.payback-curve",
  "profit-model.capital-return-velocity",
  "profit-model.profit-inflection-point",
  "financials.operating-profit",
  // financials.cash-cycle ABSORBED into the flagship profit-model.payback-curve
  // (Capital return velocity — one card, two placements). Removed from the ship
  // set so SHIP_SET still equals the catalog (the no-drift test). financials.roic
  // is deliberately NOT here: a new card, hidden from real clients until approved.
  "financials.operating-profit-by-month",
  "financials.revenue-vs-breakeven",
  "financials.reinvestment-capacity",
  "marketing.amer",
  "marketing.marketing-spend",
  "marketing.blended-cac",
  // marketing.breakeven-cm-roas ABSORBED into marketing.cm-amer (its 1 ÷ margin%
  // break-even folds into that card's note + a live break-even sub). Removed here so
  // SHIP_SET still equals the catalog (the no-drift test).
  "marketing.ltv-cac-gross",
  "marketing.revenue-by-channel",
  "marketing.revenue-over-time",
  "marketing.new-customers",
  "marketing.aov",
  "marketing.revenue-by-product",
  // marketing.units-sold merged into products.total-units (kept SHIP_SET = catalog).
  "marketing.customer-segments-rfm",
  // marketing.overdue-customers ABSORBED into marketing.customer-segments-rfm
  // (it's a row inside that card). Removed here so SHIP_SET still equals the
  // catalog (the no-drift test).
  "marketing.attribution-variance",
  "marketing.geography",
  "products.products-sold",
  "products.total-units",
  "products.product-revenue-over-time",
  "products.revenue-concentration",
  "products.units-per-product",
  "products.product-table",
  "products.movers",
  "products.gateway-products",
  "products.bought-together",
  // cohorts.headline removed (#8) — kept SHIP_SET equal to the catalog.
  "cohorts.ltv-by-first-product",
  "retention.orders-per-customer",
  // retention.days-to-2nd-order + retention.time-between-orders MERGED into
  // exec.time-between-purchases (founder 2026-07-14); removed here so SHIP_SET
  // still equals the catalog (the no-drift test).
  "retention.purchase-frequency-funnel",
  "retention.new-vs-returning",
  // retention.retention-by-first-product cut (#6) — kept SHIP_SET = catalog.
  "subscriptions.money-actual",
  "subscriptions.net-adds",
  "subscriptions.monthly-churn",
  "subscriptions.active-subscriptions",
  "subscriptions.subs-by-type",
  "subscriptions.new-vs-canceled",
  "subscriptions.cancel-curve",
  "subscriptions.base-lifetime",
  "subscriptions.cohort-retention",
  // Refund section consolidated 9→3 (founder 2026-07-13): refund-rate (headline
  // tile), refunds-by-sale-date (upgraded to the cohort card), refund-trend
  // (money-over-time flow). The 6 absorbed ids were removed here too, so
  // SHIP_SET stays equal to the catalog (the no-drift test).
  "operations.refund-rate",
  "operations.refunds-by-sale-date",
  "operations.refund-trend",
  "operations.chargeback-rate",
  "operations.chargeback-amount",
  "operations.chargebacks-pct-revenue",
  "operations.chargeback-count",
  "operations.overhead-pct-revenue",
  "operations.overhead-per-customer",
  "operations.labor-pct-revenue",
  "operations.labor-pct-cm",
  "operations.operating-leverage",
];

const SHIP_SET = new Set<string>(SHIP_SET_CARD_IDS);

/** Is this card one of the grandfathered ship-set cards? */
export function isGrandfatheredCard(cardId: string): boolean {
  return SHIP_SET.has(cardId);
}

/**
 * The "Later" shelf (founder 2026-07-14, #13/#17/#18): an OPERATOR-ONLY parking
 * area in Agency › Cards for cards deferred out of the main review flow. Clients
 * never see these until the founder promotes them; they simply sit in their own
 * tab instead of cluttering "Needs review". Not shipped to clients regardless.
 */
export const LATER_SHELF_IDS: ReadonlySet<string> = new Set<string>([
  "marketing.attribution-variance",   // #13 — locked until an ad/affiliate platform connects
  "financials.reinvestment-capacity", // #17 — deferred planning concept
  "financials.revenue-vs-breakeven",  // #18 — redundant with the monthly operating-profit view
  // Locked until the overhead/labor work lands (founder 2026-07-14): these need
  // labor split out of overhead, or multiple overhead snapshots, which don't exist
  // yet. Parked here so they stay out of review until then.
  "operations.labor-pct-revenue",
  "operations.labor-pct-cm",
  "operations.operating-leverage",
]);

/** Is this card parked on the operator-only "Later" shelf? */
export function isLaterShelf(cardId: string): boolean {
  return LATER_SHELF_IDS.has(cardId);
}

/**
 * The merge map: an ABSORBED (old, no-longer-a-catalog-entry) id → the CANONICAL
 * id that now covers it. Used by migration 0089 to move any existing approval a
 * founder already made onto the merged card, and by the tests. Keep in lockstep
 * with the placements above and the 0089 VALUES list.
 */
export const MERGED_CARD_IDS: Readonly<Record<string, string>> = {
  "marketing.mer": "exec.mer",
  "marketing.orders": "exec.orders",
  "marketing.units-sold": "products.total-units",
  "retention.repeat-rate": "exec.repeat-rate",
  "retention.days-to-2nd-order": "exec.time-between-purchases",
  "retention.time-between-orders": "exec.time-between-purchases",
  "operations.operational-drag": "exec.operational-drag",
  "financials.profit-waterfall": "exec.profit-equation",
  "financials.capital-return-velocity": "profit-model.capital-return-velocity",
  "financials.profit-inflection-point": "profit-model.profit-inflection-point",
  "cohorts.ltv-by-source": "cohorts.ltv-by-first-product",
  "cohorts.ltv-by-discount": "cohorts.ltv-by-first-product",
  "cohorts.ltv-by-affiliate": "cohorts.ltv-by-first-product",
  "subscriptions.money-projected": "subscriptions.money-actual",
  "subscriptions.money-scheduled": "subscriptions.money-actual",
  // The 'Overdue customers' win-back list is a ROW inside the Customer-segments
  // (RFM) card, never a standalone card — merging it removes the phantom review
  // entry (founder 2026-07-13: "composite sub-views must not be separate approvals").
  "marketing.overdue-customers": "marketing.customer-segments-rfm",
  // The time-of-day strip is a small bar that rides INSIDE the Revenue/Orders/
  // Customers cards, never a standalone card — folding it removes the separate
  // review row (founder 2026-07-14: it's part of another card, don't make me
  // rule on it). It still renders in-app; this only drops the approval entry.
  "exec.daypart-strip": "exec.revenue",
  // CM-LTV:CAC folded into LTV:CAC as its Profit toggle (founder 2026-07-14).
  "marketing.cm-ltv-cac": "marketing.ltv-cac-gross",
  // Exec-cockpit LTV:CAC lever and the Marketing LTV:CAC card are the SAME metric,
  // so they are ONE card with two placements now (founder 2026-07-14).
  "profit-model.ltv-cac": "marketing.ltv-cac-gross",
  // Day-1 CM-ROAS by channel + its aMER-by-channel target both removed 2026-07-14
  // (by-channel is a drill view now, not a card), so this merge entry is dropped.
  // Breakeven CM-ROAS folded into CM-aMER (founder 2026-07-13: "why double up" —
  // 1 ÷ margin% is a property of CM-aMER's margin, not a separate card). Migration
  // 0092 remaps any approval; the note/mergedFrom-only change keeps cm-amer's hash.
  "marketing.breakeven-cm-roas": "marketing.amer",
  "marketing.cm-mer": "exec.mer",
  "marketing.cm-amer": "marketing.amer",
  // The flagship Capital-Return-Velocity build (founder 2026-07-13): the Financials
  // "Your cash cycle & payback" card AND (transitively) the payback-layers card it
  // had absorbed now merge into the flagship profit-model.payback-curve, which
  // renders on BOTH pages. The flagship's prose changed (it's now the full journey
  // with the CRV headline), so it re-opens for review — correct; no migration is
  // needed because a content change re-opens approval regardless.
  "financials.cash-cycle": "profit-model.payback-curve",
  "financials.payback-layers": "profit-model.payback-curve",
};

// ---------------------------------------------------------------------------
// Where each card can be previewed live (the demo surface) + anchor registry
// ---------------------------------------------------------------------------

/** The public read-only demo route per page. Every dashboard page now has a demo
 *  surface (Financials added 2026-07-13), so "See it live" resolves for all
 *  pages; the `| null` is kept so a future page with no demo route still falls
 *  back to the disabled state via cardPreviewTarget(). */
export const DEMO_ROUTE_BY_PAGE_KEY: Readonly<Record<CardPageKey, string | null>> = {
  dashboard: "/demo/dashboard",
  marketing: "/demo/marketing",
  products: "/demo/products",
  cohorts: "/demo/cohorts",
  retention: "/demo/retention",
  subscriptions: "/demo/subscriptions",
  operations: "/demo/operations",
  financials: "/demo/financials",
};

/** Render ids that keep component-owned title/explanation copy. Most wrap a
 *  GROUP of cards; the subscription entries are separately titled source views
 *  that share one historical approval concept. "See it live" can spotlight
 *  them, but catalog name/explanation overrides must not flatten their copy. */
export const SECTION_ANCHOR_IDS: ReadonlySet<string> = new Set([
  "mkt-demand",
  "mkt-acquisition",
  "mkt-geo",
  "mkt-connect",
  "mkt-attribution-segments",
  "ops-refunds-returns",
  "ops-refund-analysis",
  "ops-chargebacks",
  "ops-efficiency",
  "subscription-leaks",
  "subscription-upcoming-charges",
  "subscription-projection",
  "subscription-plan-duration",
]);

/** Every id the web app actually emits as `data-card-anchor` (verified against
 *  the live pages 2026-07-13). "See it live" only enables when a card's placement
 *  renderId is in here AND its page has a demo route — so a button never deep-links
 *  to a card that can't be found (no dead links). */
export const EMITTED_ANCHOR_IDS: ReadonlySet<string> = new Set([
  // Consolidated refund trio (2026-07-13) — Hideable ids on OperationsPage:
  "ops-refunds-returns",
  "ops-refunds-by-sale-date",
  "ops-refund-trend",
  "mkt-demand",
  "mkt-acquisition",
  "mkt-geo",
  "mkt-connect",
  "mkt-attribution-segments",
  "ops-refunds-returns",
  "ops-refund-analysis",
  "ops-chargebacks",
  "ops-efficiency",
  "revenue",
  "orders",
  "customers",
  "repeat",
  "refund",
  "mer",
  "cac",
  "op-drag",
  "top-products",
  "equation-strip",
  "revenue-trend",
  "time-between",
  "profit-model",
  "pm-contribution-margin",
  "pm-ltv-cac",
  "pm-acquisition-economics",
  "pm-operational-efficiency",
  "pm-capital-return-velocity",
  "pm-profit-inflection-point",
  "pm-payback-curve",
  "products-sold",
  "total-units",
  "product-revenue-trend",
  "revenue-concentration",
  "units-per-product",
  "product-movers",
  "bought-together",
  "gateway-products",
  "product-table",
  "cv-breakdown",
  "cv-gateway",
  "ret-repeat",
  "ret-opc",
  "ret-time-between",
  "ret-funnel",
  "ret-nvr",
  "sub-money-30d",
  "subscription-upcoming-charges",
  "subscription-projection",
  "net-adds",
  "monthly-churn",
  "active-subs",
  "subscription-leaks",
  "subs-by-type",
  "subs-new-vs-canceled",
  "churn-rate-by-month",
  "cancel-curve",
  "subscription-plan-duration",
  "base-lifetime",
  "sub-cohort-matrix",
  "fin-operating-profit",
  "fin-crv",
  "fin-pip",
  "fin-payback-layers",
  "fin-roic",
  "fin-waterfall",
  "fin-op-profit-trend",
  "fin-rev-vs-breakeven",
  "fin-reinvest",
  "avg-order-value",
  "mer-blended",
  "amer-acquisition",
  "marketing-spend",
  "blended-cac",
  "ltv-cac",
  "new-customers",
  "refund-rate",
  "refunded",
  "refunds-of-revenue",
  "avg-refund-value",
  "ops-refund-by-product",
  "ops-refund-trend",
  "chargeback-rate",
  "chargebacks",
  "chargebacks-of-revenue",
  "chargeback-count",
  "operational-drag",
  "overhead-of-revenue",
  "overhead-per-customer",
]);

// ---------------------------------------------------------------------------
// Lookups + approval math
// ---------------------------------------------------------------------------

const CATALOG_BY_ID = new Map<string, CardCatalogEntry>(CARD_CATALOG.map((c) => [c.id, c]));

export function cardCatalogEntry(id: string): CardCatalogEntry | undefined {
  return CATALOG_BY_ID.get(id);
}

// ---------------------------------------------------------------------------
// Single source of truth for the client ⓘ (founder 2026-07-13: "variance
// between the live page and what you're saying on each card")
// ---------------------------------------------------------------------------

/** The client-visible copy the live ⓘ should show for a card. */
export interface CardExplainSource {
  /** The catalog's canonical "What it is" (client voice). */
  what: string;
  /** The catalog's canonical "Note" (client voice), or undefined when the card
   *  has no Note in the catalog (the component's own Note line is the fallback). */
  note?: string;
}

/**
 * renderId → the catalog's client copy for that card — but ONLY when the renderId
 * unambiguously identifies ONE titled card. A section-wrapper renderId (shared by
 * several cards under one <Hideable>) resolves to NOTHING, so a card rendered
 * under a shared section anchor keeps its own component copy (a documented
 * exception, never the wrong card's text).
 *
 * This is THE join the client render choke point uses so the live ⓘ and the
 * review row can't drift: the live "What it is" / "Note" resolve as
 *   operator override → this catalog copy → the component's local string.
 */
const EXPLAIN_SOURCE_BY_RENDER_ID: Map<string, CardExplainSource> = (() => {
  // Count owners per renderId so a renderId shared by >1 entry never resolves.
  const owners = new Map<string, string[]>();
  for (const c of CARD_CATALOG) {
    for (const p of c.placements) {
      (owners.get(p.renderId) ?? owners.set(p.renderId, []).get(p.renderId)!).push(c.id);
    }
  }
  const out = new Map<string, CardExplainSource>();
  for (const c of CARD_CATALOG) {
    for (const p of c.placements) {
      if (SECTION_ANCHOR_IDS.has(p.renderId)) continue; // section wrapper — no single card
      if ((owners.get(p.renderId)?.length ?? 0) !== 1) continue; // shared → ambiguous
      out.set(p.renderId, { what: c.definition, note: c.note });
    }
  }
  return out;
})();

/**
 * The catalog's client "What it is" / "Note" for a render id, or undefined when
 * the renderId is a section wrapper / shared / unknown (→ the component keeps its
 * own copy). Pure + shared so the web ⓘ, the operator/demo surfaces, and the tests
 * all resolve identically.
 */
export function cardExplainSourceByRenderId(renderId: string | null | undefined): CardExplainSource | undefined {
  if (!renderId) return undefined;
  return EXPLAIN_SOURCE_BY_RENDER_ID.get(renderId);
}

/** Every catalog card id (in catalog order). */
export function allCardIds(): string[] {
  return CARD_CATALOG.map((c) => c.id);
}

/** The primary placement (the page a merged card is grouped under). */
export function primaryPlacement(entry: CardCatalogEntry): CardPlacement {
  return entry.placements[0]!;
}

/** "Exec dashboard + Marketing" — the distinct page names a card appears on. */
export function cardPagesLabel(entry: CardCatalogEntry): string {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const p of entry.placements) {
    if (!seen.has(p.page)) { seen.add(p.page); names.push(p.page); }
  }
  return names.join(" + ");
}

export interface CardPreviewTarget {
  pageKey: CardPageKey;
  page: string;
  /** The demo path incl. the ?spotlight= param, e.g. "/demo/marketing?spotlight=blended-cac". */
  path: string;
  renderId: string;
}

/**
 * The best "See it live" target for a card, or null when none of its placements
 * can be previewed (no demo surface for its pages, or the card isn't an emitted
 * anchor). Picks the FIRST placement that both has a demo route and is a real
 * emitted anchor — so a merged card whose primary page is Financials (no demo)
 * still previews on its Dashboard placement.
 */
export function cardPreviewTarget(entry: CardCatalogEntry): CardPreviewTarget | null {
  for (const p of entry.placements) {
    const route = DEMO_ROUTE_BY_PAGE_KEY[p.pageKey];
    if (route && EMITTED_ANCHOR_IDS.has(p.renderId)) {
      return { pageKey: p.pageKey, page: p.page, path: `${route}?spotlight=${encodeURIComponent(p.renderId)}`, renderId: p.renderId };
    }
  }
  return null;
}

/** The placement renderIds whose visible title a rename override replaces — the
 *  single titled cards (section wrappers excluded). The client name-override map
 *  is emitted for exactly these ids. */
export function cardRenameRenderIds(entry: CardCatalogEntry): string[] {
  const out: string[] = [];
  for (const p of entry.placements) {
    if (!SECTION_ANCHOR_IDS.has(p.renderId) && !out.includes(p.renderId)) out.push(p.renderId);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Company-wide badge (founder 2026-07-13: "add a button for 'company wide'")
// ---------------------------------------------------------------------------

/** Canonical ids that render the "🌐 Blended" badge today (hardcoded companyWide
 *  on their MetricCard) — the pre-seeded default the operator toggle starts from.
 *  (The field/flag names stay `companyWide` — the badge WORD is "Blended".) */
export const COMPANY_WIDE_DEFAULT_IDS: ReadonlySet<string> = new Set([
  "exec.mer",
  "exec.acquisition-cac",
  "exec.operational-drag",
  "marketing.amer",
  "marketing.marketing-spend",
  "marketing.blended-cac",
  "marketing.ltv-cac-gross",
  "operations.overhead-pct-revenue",
  "operations.overhead-per-customer",
]);

/** renderIds that render via MetricCard, so they CAN show the badge. A card with
 *  none of these placements gets a disabled toggle (its card type can't carry it). */
export const COMPANY_WIDE_CAPABLE_RENDER_IDS: ReadonlySet<string> = new Set([
  "mer",
  "cac",
  "op-drag",
  "mer-blended",
  "amer-acquisition",
  "marketing-spend",
  "blended-cac",
  "ltv-cac",
  "avg-order-value",
  "orders",
  "new-customers",
  "refund-rate",
  "refunded",
  "refunds-of-revenue",
  "avg-refund-value",
  "chargeback-rate",
  "chargebacks",
  "chargebacks-of-revenue",
  "chargeback-count",
  "operational-drag",
  "overhead-of-revenue",
  "overhead-per-customer",
]);

/** Does this card default to company-wide (the current hardcoded behaviour)? */
export function cardCompanyWideDefault(entry: CardCatalogEntry): boolean {
  return COMPANY_WIDE_DEFAULT_IDS.has(entry.id);
}

/** Can this card show the company-wide badge at all (is it a MetricCard)? */
export function cardCompanyWideCapable(entry: CardCatalogEntry): boolean {
  return entry.placements.some((p) => COMPANY_WIDE_CAPABLE_RENDER_IDS.has(p.renderId));
}

/**
 * The company-wide badge a CLIENT actually sees, from the operator's override and
 * the card's coded default (founder's approved design 2026-07-13). The toggle's
 * two directions mean DIFFERENT things:
 *   - override === true  → force the badge ON. Pure labeling: the operator is
 *     telling clients "the filters don't move this number", even on a card whose
 *     math isn't inherently company-wide. Instant, unchanged behaviour.
 *   - override === false → an override of false NEVER hides a badge whose MATH is
 *     genuinely company-wide (companyWideDefault === true). Turning the toggle OFF
 *     on such a card is a REQUEST to rebuild the math so the number follows the
 *     product-filter rail — a build, not a label change — so the badge KEEPS
 *     showing (code truth wins) until that math ships. When it ships, the card
 *     stops being companyWideDefault=true and the badge drops on its own.
 *   - override null / undefined → the coded default.
 * So effectively: `override === true ? true : companyWideDefault`. Pure + shared
 * so the client render path and the API/tests can never disagree.
 */
export function effectiveCompanyWideBadge(
  override: boolean | null | undefined,
  companyWideDefault: boolean | null | undefined,
): boolean {
  if (override === true) return true;
  return companyWideDefault === true;
}

/**
 * Derive the operator-facing status for one card given its stored approval row.
 * "changed" = approved once, but the definition text has since moved.
 */
export function deriveCardStatus(
  entry: Pick<CardCatalogEntry, "definition" | "arithmetic" | "example">,
  approval: CardApprovalRow | undefined,
): CardDerivedStatus {
  if (!approval) return "pending";
  if (approval.status === "rejected") return "rejected";
  return approval.definitionHash === cardDefinitionHash(entry) ? "approved" : "changed";
}

/**
 * The hidden set for an account. THE one rule (shared by the API endpoint, the
 * client render gate, and the tests):
 *  - demo/test/resettable (isRealClient=false) → NOTHING hidden.
 *  - real clients:
 *      • grandfathered card → hidden ONLY if explicitly rejected.
 *      • new card (not in ship set) → hidden UNLESS currently approved.
 */
export function computeHiddenCardIds(opts: {
  approvals: CardApprovalRow[];
  isRealClient: boolean;
  catalog?: CardCatalogEntry[];
}): string[] {
  if (!opts.isRealClient) return [];
  const byId = new Map<string, CardApprovalRow>(opts.approvals.map((a) => [a.cardId, a]));
  const catalog = opts.catalog ?? CARD_CATALOG;
  const hidden: string[] = [];
  for (const entry of catalog) {
    const status = deriveCardStatus(entry, byId.get(entry.id));
    if (SHIP_SET.has(entry.id)) {
      if (status === "rejected") hidden.push(entry.id);
    } else {
      if (status !== "approved") hidden.push(entry.id);
    }
  }
  return hidden;
}

// ---------------------------------------------------------------------------
// Merge remap (migration 0089 logic, unit-tested here so the SQL can mirror it)
// ---------------------------------------------------------------------------

/** A candidate card_approvals row competing to become the canonical merged row. */
export interface MergeApprovalCandidate {
  cardId: string;
  status: string; // 'approved' | 'rejected' | 'question' | 'pending'
  /** approved_at as epoch ms (tie-break: most recently touched wins). */
  approvedAtMs: number;
  /** whether the row carries a draft note or display name (last tie-break). */
  hasDraftOrName: boolean;
}

/** Decisiveness rank: a decision (approved/rejected) beats a question beats a
 *  bare pending note-row. Mirrors the 0089 CASE expression. */
export function approvalDecisiveness(status: string): number {
  if (status === "approved" || status === "rejected") return 3;
  if (status === "question") return 2;
  return 1;
}

/**
 * Pick the row that should survive when several approvals collapse onto one
 * canonical id (the founder decided differently on two placements of the same
 * card). Most decisive wins; ties break to the most recently touched, then to a
 * row that already carries a draft/name so nothing is silently dropped. Pure —
 * the migration SQL implements the identical ordering (ROW_NUMBER OVER ...).
 */
export function resolveMergedApprovalWinner(cands: MergeApprovalCandidate[]): MergeApprovalCandidate | null {
  if (cands.length === 0) return null;
  return [...cands].sort((a, b) => {
    const d = approvalDecisiveness(b.status) - approvalDecisiveness(a.status);
    if (d !== 0) return d;
    if (b.approvedAtMs !== a.approvedAtMs) return b.approvedAtMs - a.approvedAtMs;
    return (b.hasDraftOrName ? 1 : 0) - (a.hasDraftOrName ? 1 : 0);
  })[0]!;
}
